import { isSessionDropping401 } from "./unauthorized";
import { xhrRequest } from "./xhrTransport";
import type {
  RestClientHooks,
  RestInterceptor,
  RestResponse,
} from "@app/features/platform/types/TransportTypes";
// Kök: lib/config/RuntimeConfig.ts (devFallback davranışı birebir taşındı —
// burada duplike edilmedi, tek kaynaktan okunur).
import { RuntimeConfig } from "@/lib/config/RuntimeConfig";
import { RelationshipTypes, type RelationshipType } from "@/lib/constants/UserConstants";
import { ChannelTypes, normalizeChannelTypeCode, channelTypeToWireString } from "@/lib/constants/ChannelConstants";

export const API_BASE = RuntimeConfig.apiBase;
// Sprint 17.C — FILES_BASE kaldırıldı (tebhos_files servisi SİLİNDİ).
// Dosyalar artık cdn.tebhos.com R2 custom domain'inden doğrudan serve edilir.
// Sprint 17.B migration 0054 mevcut localhost:4000/files URL'lerini rewrite etti.
// EMBED_BASE (:4001) 2026-08-02'de KALDIRILDI — hiçbir zaman gerçek bir servis
// yoktu (ölü uç); tek çağıran UrlEmbed.tsx artık çalışan media-proxy /og
// ucunu (fetchOgMetadata, lib/media/proxyUrl.ts) kullanıyor. Bkz.
// scratchpad/i18n_raporlar/eksik_G_embed_son.md.

/**
 * Sprint 17.D — Transform polymorphic /users/@me/channels response → home-sidebar shape.
 *
 * Backend (Sprint 17.A) `{ items: DMChannel[] }` döner. UI shape "recentDMs"
 * (eski direct_message domain'in döndürdüğü). 1-on-1 DM için other user'ı
 * çıkarırız, Group DM için kendi shape'i (icon, recipients[]).
 *
 * Eski `transformConversations` API'si SİLİNDİ — `/direct-messages/conversations`
 * endpoint'i Sprint 17.B'de kaldırıldı; bu helper artık polymorphic data alır.
 */
export function transformDMChannels(
  data: { items: DMChannel[] } | DMChannel[],
  myProfileId: string
): { recentDMs: any[] } {
  const items = Array.isArray(data) ? data : data?.items || [];
  const list = items.map((ch) => {
    // Kaynak numeric ChannelType: backend `channel_type` (i32) öncelikli okunur
    // (apiFetch camelCase sonrası `channelType`); henüz migrasyonsuz wire
    // yollarından (WS READY) gelen eski string `type` normalize edilir.
    const channelType = normalizeChannelTypeCode((ch as any).channelType ?? (ch as any).channel_type ?? ch.type);
    const isGroupDM = channelType === ChannelTypes.GROUP_DM;
    // 1-on-1: other recipient = self HARİÇ ilk recipient
    const others = ch.recipients.filter((r) => r.id !== myProfileId);
    const primary = others[0] || ch.recipients[0] || { id: "", name: "?", imageUrl: "" };
    return {
      // channelId = sidebar item URL'si (yeni polymorphic path)
      channelId: ch.id,
      channelType,
      // Group DM için recipients listesi (avatar mozaik)
      recipients: ch.recipients,
      groupName: isGroupDM ? ch.name || null : null,
      groupIcon: isGroupDM ? ch.icon || null : null,
      // 1-on-1 için primary other user (eski `profile` field UI uyumu için korunur)
      profile: {
        id: primary.id,
        name: isGroupDM ? ch.name || others.map((o) => o.name).join(", ") || "Group" : primary.name,
        imageUrl: isGroupDM ? ch.icon || "" : primary.imageUrl,
      },
      // Eski memberId/serverId yapısı (compat — DM context menu, livekit dm vs kullanıyor).
      // Sprint 17.D sonrası UI kademeli olarak `channelId` direct kullanacak.
      memberId: primary.id,
      serverId: "home",
      // G8-3 A1 — Kaynak `getSortedDmChannels` sıralaması için `last_message_id`
      // (snowflake string) taşınır (DmChannelUtils pinli-üstte + son-mesaj-sıralı).
      lastMessageId:
        (ch as any).lastMessageId ?? (ch as any).last_message_id ?? null,
    };
  });
  // Kaynak 1-1: requestCount SÖKÜLDÜ — message-request kavramı yok.
  return { recentDMs: list };
}

/* ── Token helpers (Feature 14 — multi-account refactor) ──────────────
 *
 * Token storage AccountManagerStore'da (localStorage persist) — saklama YERİ
 * değişmedi (N2 paritesi). OPAK-OTURUM (2026-07-18, Kaynak 1-1): backend
 * artık JWT access/refresh çifti DEĞİL tek kalıcı opak oturum-token'ı
 * (`flx_...`) veriyor. Token `accessToken` alanında saklanır; `refreshToken`
 * alanı ÖLÜ ("" yazılır — store şekli korunur, eski hesap kayıtları bozulmaz).
 *
 * - setTokens(token, "", identity?): identity VERİLİRSE userId'ye göre upsert
 *   eder (Kaynak `SessionManager.login` 1-1 — ÇOKLU-HESAP İZOLASYONU 2026-07-18
 *   düzeltmesi: önceden accounts.length'e göre dallanıyordu, bu yüzden "Hesap
 *   Ekle" ile ikinci bir hesaba giriş MEVCUT hesabın token'ını EZİYORDU, yeni
 *   bir hesap eklemiyordu. identity.id verilince artık DAİMA doğru hesaba
 *   (varsa günceller, yoksa ekler + aktif yapar) yazılır.
 *   identity YOKSA (ör. passwordChangeComplete token-rotasyonu — yeni bir
 *   kimlik yok, sadece mevcut oturumun token'ı yenileniyor): eski davranış —
 *   hiç hesap yoksa ekle, varsa MEVCUT aktif hesabı güncelle.
 * - getAccessToken: mevcut hesabın oturum-token'ı.
 * - clearTokens: mevcut hesabı çıkar (diğer hesaplar kalır).
 */

import {
  AccountManagerStore,
  clearCurrentAccount,
  getCurrentAccessToken,
  getCurrentRefreshToken,
  setCurrentTokens,
} from "@app/features/auth/state/AccountManagerStore";

export interface TokenIdentity {
  id: string;
  name?: string | null;
  imageUrl?: string | null;
  /** Hesap satırındaki `kullanıcıadı#0000` etiketi için (bkz. AccountEntry). */
  username?: string | null;
  discriminator?: string | null;
}

export function setTokens(access: string, refresh: string = "", identity?: TokenIdentity) {
  const store = AccountManagerStore;
  if (identity?.id) {
    store.addAccount({
      id: identity.id,
      name: identity.name ?? null,
      imageUrl: identity.imageUrl ?? null,
      ...(identity.username ? { username: identity.username } : {}),
      ...(identity.discriminator ? { discriminator: identity.discriminator } : {}),
      accessToken: access,
      refreshToken: refresh,
    });
    return;
  }
  if (store.accounts.length === 0 || !store.currentAccountId) {
    store.addAccount({
      id: `account-${Math.random().toString(36).slice(2, 10)}`,
      name: null,
      imageUrl: null,
      accessToken: access,
      refreshToken: refresh,
    });
  } else {
    setCurrentTokens(access, refresh);
  }
}

export function getAccessToken(): string | null {
  return getCurrentAccessToken();
}

export function getRefreshToken(): string | null {
  return getCurrentRefreshToken();
}

export function clearTokens() {
  clearCurrentAccount();
}

/**
 * 7.1.11 — Refresh JWT'sinin `jti` claim'ini çıkar. OPAK-OTURUM (2026-07-18)
 * SONRASI ÖLÜ YOL: refresh token artık yok ("" saklanır) → her zaman null
 * döner. Yalnız eski (JWT'li) hesap kayıtları için geriye-dönük çalışır.
 * Yeni "Bu Cihaz" tespiti: `currentSessionHashHex()` (aşağıda) —
 * sha256(oturum-token'ı) = backend auth_sessions PK'sı.
 */
export function getCurrentRefreshJti(): string | null {
  if (typeof window === "undefined") return null;
  const refresh = getRefreshToken();
  if (!refresh) return null;
  const parts = refresh.split(".");
  if (parts.length !== 3) return null;
  try {
    const payloadB64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(payloadB64));
    return typeof payload?.jti === "string" ? payload.jti : null;
  } catch {
    return null;
  }
}

/**
 * OPAK-OTURUM (2026-07-18) — mevcut oturumun auth_sessions kimliği:
 * sha256(oturum-token'ı) hex (Kaynak getTokenIdHash 1-1). "Bu Cihaz"
 * rozeti + kendi-cihaz kick kontrolü bu değerle karşılaştırır.
 * Token yoksa / WebCrypto yoksa null.
 */
export async function currentSessionHashHex(): Promise<string | null> {
  if (typeof window === "undefined") return null;
  const token = getAccessToken();
  if (!token) return null;
  try {
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(token));
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
}

/* ── Fetch wrapper ──────────────────────────────────────────────────── */

// Convert snake_case keys to camelCase recursively (for API responses)
// Exported: WS event payload'ları (snake_case) da bu dönüşümle GET response
// şekliyle (camelCase) birebir aynı hale getirilir — bkz. use-chat-socket.ts.
export function toCamelCase(obj: any): any {
  if (Array.isArray(obj)) return obj.map(toCamelCase);
  if (obj !== null && typeof obj === "object" && !(obj instanceof Date)) {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k.replace(/_([a-z])/g, (_, c) => c.toUpperCase()), toCamelCase(v)])
    );
  }
  return obj;
}

// Convert camelCase keys to snake_case recursively (for API requests)
function toSnakeCase(obj: any): any {
  if (Array.isArray(obj)) return obj.map(toSnakeCase);
  if (obj !== null && typeof obj === "object" && !(obj instanceof Date)) {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k.replace(/([A-Z])/g, (_, c) => `_${c.toLowerCase()}`), toSnakeCase(v)])
    );
  }
  return obj;
}

// Convert query string keys camelCase → snake_case. Values pass through.
// Backend DTOs use snake_case; this lets call sites pass `?serverId=X` and
// have it become `?server_id=X` automatically. Already-snake keys stay
// unchanged (no uppercase to translate). Path part untouched.
function toSnakeCaseQuery(path: string): string {
  const qIdx = path.indexOf("?");
  if (qIdx === -1) return path;
  const base = path.slice(0, qIdx);
  const incoming = new URLSearchParams(path.slice(qIdx + 1));
  const outgoing = new URLSearchParams();
  for (const [k, v] of incoming) {
    outgoing.append(
      k.replace(/([A-Z])/g, (_, c) => `_${c.toLowerCase()}`),
      v
    );
  }
  const qs = outgoing.toString();
  return qs ? `${base}?${qs}` : base;
}

/* ── Global interceptor (Kaynak RestTransport.installHooks minimal 1-1) ──
 *
 * Kaynak `http.installHooks({intercept})` karşılığı: CaptchaInterceptor gibi
 * global tüketiciler her yanıtı görebilir; `retry(extraHeaders)` orijinal
 * isteği ekstra başlıklarla AYNEN tekrarlar, `reject(err)` isteği o hatayla
 * düşürür. Sözleşme: src/features/platform/types/TransportTypes.ts
 * `RestInterceptor` (zaten portlu). Kaynak'daki plan/attempt makinesinin
 * tamamı DEĞİL — apiFetch'in ihtiyacı olan intercept+retry çekirdeği.
 */
let globalIntercept: RestInterceptor | undefined;

export function installHooks(hooks: RestClientHooks): void {
  globalIntercept = hooks.intercept;
}

async function toRestResponse(res: Awaited<ReturnType<typeof xhrRequest>>): Promise<RestResponse> {
  const text = await res.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : undefined;
  } catch {
    body = undefined;
  }
  return { ok: res.ok, status: res.status, statusText: res.statusText, headers: res.headers, body, text };
}

/**
 * Kaynak `invokeInterceptor` karşılığı: yanıtı global interceptor'a ver.
 * Interceptor bir Promise<RestResponse> döndürürse (captcha çözüldü → retry
 * zinciri) nihai yanıt onun sonucudur; `reject` çağrılırsa o hata fırlatılır;
 * aksi halde orijinal yanıt aynen akar.
 */
async function runGlobalIntercept(
  res: Awaited<ReturnType<typeof xhrRequest>>,
  replay: (extraHeaders: Record<string, string>) => Promise<Awaited<ReturnType<typeof xhrRequest>>>,
): Promise<Awaited<ReturnType<typeof xhrRequest>>> {
  if (!globalIntercept) return res;
  let finalRes = res;
  let captured: Error | null = null;
  const retry = async (extraHeaders: Record<string, string>): Promise<RestResponse> => {
    finalRes = await replay(extraHeaders);
    return toRestResponse(finalRes);
  };
  const reject = (err: Error) => {
    captured = err;
  };
  const outcome = globalIntercept(await toRestResponse(res), retry, reject);
  if (outcome && typeof (outcome as Promise<unknown>).then === "function") {
    try {
      await outcome;
    } catch (err) {
      if (captured) throw captured;
      throw err;
    }
  }
  if (captured) throw captured;
  return finalRes;
}

/* ── Hız sınırı temposu (kaynak-referans RestTransport "pacing" 1-1) ──
 *
 * Kaynak-referans `http` istemcisi bir yol 429 aldığında o yolu bekleme süresi
 * boyunca "tempolu" işaretler; süre dolmadan gelen aynı yol istekleri sunucuya
 * HİÇ gitmez, yerelde üretilmiş bir 429 ile hemen reddedilir (varsayılan
 * "strict" kip). NEDEN: dolu kovaya art arda istek atmak hem kovayı hiç
 * boşaltmaz hem de küresel sınırı (50 istek/sn) tüketir. apiFetch de aynı
 * davranışı izler ki iki istemci 429 karşısında farklı davranmasın.
 */
type PacingEntry = { until: number; note?: string };
const pacing = new Map<string, PacingEntry>();

function pacingKeyFor(path: string): string {
  const q = path.indexOf("?");
  return q === -1 ? path : path.slice(0, q);
}

function consultPacing(key: string): PacingEntry | null {
  const entry = pacing.get(key);
  if (!entry) return null;
  if (entry.until <= Date.now()) {
    pacing.delete(key);
    return null;
  }
  return entry;
}

function readRetryAfterSeconds(raw: string | undefined): number | null {
  if (!raw) return null;
  const numeric = Number(raw);
  if (Number.isFinite(numeric)) return numeric;
  const date = Date.parse(raw);
  if (!Number.isFinite(date)) return null;
  return Math.max(0, (date - Date.now()) / 1000);
}

function recordPacing(key: string, headers: Record<string, string>, body: unknown): void {
  const resetAfter = Number(headers["x-ratelimit-reset-after"]);
  const headerMs = Number.isFinite(resetAfter) && resetAfter > 0 ? resetAfter * 1000 : null;
  const retryAfterSeconds = readRetryAfterSeconds(headers["retry-after"]);
  const bodyRetry =
    body && typeof body === "object" ? (body as { retry_after?: unknown }).retry_after : undefined;
  // Başlık yoksa (farklı kökende gizlenmiş olabilir) gövdedeki ondalıklı süre
  // kullanılır; hiçbiri yoksa kaynak-referans gibi 1 sn.
  const bodyMs = typeof bodyRetry === "number" && bodyRetry > 0 ? bodyRetry * 1000 : null;
  const ms =
    headerMs ?? (retryAfterSeconds !== null && retryAfterSeconds > 0 ? retryAfterSeconds * 1000 : null) ?? bodyMs ?? 1000;
  const note =
    body && typeof body === "object" && typeof (body as { message?: unknown }).message === "string"
      ? ((body as { message: string }).message)
      : undefined;
  pacing.set(key, { until: Date.now() + ms, note });
}

/** Süre dolmadan tekrarlanan isteğe sunucuya gitmeden verilen yerel 429. */
function pacedError(hit: PacingEntry): Error & { status: number; body: any } {
  const remaining = Math.max(0, hit.until - Date.now());
  const body = {
    code: "RATE_LIMITED",
    message: hit.note ?? "Hız sınırlamasına takıldın.",
    retry_after: remaining / 1000,
    global: false,
  };
  const e = new Error(body.message) as Error & { status: number; body: any };
  e.status = 429;
  e.body = body;
  return e;
}

/** Hassas uç gövdesine eklenen TEK sudo alanı (wire adları; bkz. auth/utils/SudoFields). */
export type SudoBody = { password?: string; mfa_code?: string; sudo_proof?: string };

// OPAK-OTURUM (2026-07-18, Kaynak 1-1) — refresh-akışı SÖKÜLDÜ:
// `tryRefreshToken`/`isTokenExpiringSoon`/401-refresh-retry kalktı. Oturum
// token'ı kalıcıdır (süresi dolmaz); 401 = oturum sunucuda iptal edilmiş
// (logout/reset/revoke) → tokenlar temizlenir, korumalı sayfada /sign-in'e
// yönlendirilir (Kaynak oturum-düşmüş davranışı).

/**
 * Kaynak-referans `AuthenticationCommands.withPlatformHeader`: kimlik uçları
 * (`/auth/*`) arayüz dilini `Accept-Language` ile yollar. Sunucu hata
 * iletilerini bu dilde yazar; oturum henüz yokken (giriş, kayıt, parola
 * sıfırlama) kayıtlı dil bilinmediği için tek dil kaynağı budur. Tarayıcının
 * kendi başlığı arayüz dilinden farklı olabilir (ör. Türkçe tarayıcıda İngilizce
 * arayüz). Dil, arayüzün uyguladığı değerden (`applyLocaleChange` → `locale`) okunur.
 */
function withAuthLocale(path: string, headers: Record<string, string>): void {
  if (!path.startsWith("/auth/") || Object.keys(headers).some((h) => h.toLowerCase() === "accept-language")) return;
  try {
    const locale = typeof window === "undefined" ? null : window.localStorage.getItem("locale");
    if (locale) headers["Accept-Language"] = locale;
  } catch {
    // Depolama kapalıysa tarayıcının kendi başlığı kalır.
  }
}

export async function apiFetch<T = any>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getAccessToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options.headers as Record<string, string>) || {}),
  };
  withAuthLocale(path, headers);
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  // Auto-convert request body camelCase → snake_case
  let body = options.body;
  let sentJson: unknown = null;
  if (typeof body === "string") {
    try {
      const parsed = JSON.parse(body);
      sentJson = toSnakeCase(parsed);
      body = JSON.stringify(sentJson);
    } catch {
      /* not JSON, send as-is */
    }
  }
  // Auto-convert query string keys camelCase → snake_case
  const finalPath = toSnakeCaseQuery(path);
  const pacingKey = pacingKeyFor(finalPath);
  const pacingHit = consultPacing(pacingKey);
  if (pacingHit) throw pacedError(pacingHit);
  // Transport: XHR (Kaynak/Discord paterni — birleşik client, upload-progress yeteneği).
  let res = await xhrRequest(`${API_BASE}${finalPath}`, {
    method: options.method,
    headers,
    body: body as XMLHttpRequestBodyInit | null | undefined,
    signal: options.signal ?? null,
  });

  // Kaynak reactToReply: yanıt önce global interceptor'dan geçer (CAPTCHA_REQUIRED
  // → CaptchaModal → X-Captcha-Token/X-Captcha-Type başlıklarıyla retry).
  res = await runGlobalIntercept(res, (extraHeaders) =>
    xhrRequest(`${API_BASE}${finalPath}`, {
      method: options.method,
      headers: { ...headers, ...extraHeaders },
      body: body as XMLHttpRequestBodyInit | null | undefined,
      signal: options.signal ?? null,
    }),
  );

  // OPAK-OTURUM (2026-07-18) — 401 = oturum sunucuda geçersiz (iptal edilmiş
  // ya da hiç yok). Refresh-retry YOK (Kaynak'da refresh yok). Token-üreten
  // uçlarda (login/register/admin-login) 401 normal hata olarak akar; diğer
  // uçlarda oturum-düşmüş akışı: tokenları temizle + korumalı sayfada
  // /sign-in'e yönlendir (public sayfalar açık kalır).
  // DALGA-2 İŞ-4 (2026-07-18): /auth/admin-login + /auth/admin-verify-otp
  // path'leri listeden SÖKÜLDÜ — backend uçları kaldırıldı (Kaynak'da admin
  // login ucu yok; admin kimliği :3020 panel OAuth2 akışı).
  const isTokenIssuingPath =
    path.startsWith("/auth/login") ||
    path.startsWith("/auth/register");
  let sessionDropped = res.status === 401 && !isTokenIssuingPath && !!token;
  if (sessionDropped) {
    let unauthorizedBody: unknown = null;
    try {
      unauthorizedBody = JSON.parse(await res.text());
    } catch {}
    sessionDropped = isSessionDropping401(sentJson, unauthorizedBody);
  }
  if (sessionDropped) {
    clearTokens();
    if (typeof window !== "undefined") {
      const path = window.location.pathname;
      const isProtected = ["/channels", "/account", "/settings", "/discovery", "/inventory"].some((p) =>
        path.startsWith(p),
      );
      if (isProtected) window.location.href = "/sign-in";
    }
    throw new Error("Session expired");
  }

  if (!res.ok) {
    const errBody = await res.text();
    let msg = errBody;
    let parsedBody: any = null;
    try {
      parsedBody = JSON.parse(errBody);
      msg = parsedBody.message || parsedBody.error || errBody;
      // Alan hatalı yanıtta (INVALID_FORM_BODY) üst ileti İngilizce gibi dillerde
      // genel "Invalid form body." olur; asıl açıklama `errors[].message`'dadır
      // (sunucu istek dilinde yazar). `err.message` okuyan eski ekranlar da doğru
      // metni göstersin diye kaynak-referans `extractErrorMessage` gibi alan
      // iletileri birleştirilir.
      if (parsedBody?.code === "INVALID_FORM_BODY" && Array.isArray(parsedBody.errors)) {
        const fieldMessages = Array.from(
          new Set(
            parsedBody.errors
              .map((e: { message?: unknown }) => e?.message)
              .filter((m: unknown): m is string => typeof m === "string" && m.length > 0),
          ),
        ).join(" ");
        if (fieldMessages) msg = fieldMessages;
      }
    } catch {}
    if (res.status === 429) recordPacing(pacingKey, res.headers, parsedBody);
    // DALGA-2 (2026-07-18): ham hata gövdesi hataya iliştirilir — Kaynak
    // HttpError.body karşılığı (login 403 IP_AUTHORIZATION_REQUIRED yanıtındaki
    // ticket/email alanlarını AuthProvider.login okur; gövde snake_case HAM).
    const e = new Error(msg) as Error & { status: number; body: any };
    e.status = res.status;
    e.body = parsedBody;
    throw e;
  }
  // Handle empty responses (204, etc.)
  const text = await res.text();
  if (!text) return {} as T;
  const parsed = JSON.parse(text);
  return toCamelCase(parsed) as T;
}

/**
 * Sprint 3.7 — apiFetch sibling that exposes response headers.
 *
 * Pagination endpoint'lerinde `X-Total-Count` gibi header'lara erişmek için.
 * apiFetch normalde sadece body döner; bu helper `{ data, headers }` döner.
 *
 * Auth refresh akışı KORUNMAZ (tek-shot); pagination çağrıları çoğunlukla
 * tab açılışında yapılır, refresh gerekiyorsa apiFetch zaten me() vs.
 * çağrısında halletmiş olur.
 */
export async function apiFetchRaw<T = any>(
  path: string,
  options: RequestInit = {}
): Promise<{ data: T; headers: Headers }> {
  const token = getAccessToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options.headers as Record<string, string>) || {}),
  };
  withAuthLocale(path, headers);
  if (token) headers.Authorization = `Bearer ${token}`;
  let body = options.body;
  if (typeof body === "string") {
    try {
      body = JSON.stringify(toSnakeCase(JSON.parse(body)));
    } catch {
      /* not JSON */
    }
  }
  const finalPath = toSnakeCaseQuery(path);
  const res = await xhrRequest(`${API_BASE}${finalPath}`, {
    method: options.method,
    headers,
    body: body as XMLHttpRequestBodyInit | null | undefined,
    signal: options.signal ?? null,
  });
  if (!res.ok) {
    const errBody = await res.text();
    let msg = errBody;
    try {
      const p = JSON.parse(errBody);
      msg = p.message || p.error || errBody;
    } catch {}
    const e = new Error(msg) as Error & { status: number };
    e.status = res.status;
    throw e;
  }
  const text = await res.text();
  const data = text ? (toCamelCase(JSON.parse(text)) as T) : ({} as T);
  // xhrRequest plain Record döndürür → callers Headers bekliyor (.get()) → sarmala.
  return { data, headers: new Headers(res.headers) };
}

/* ── Auth endpoints ─────────────────────────────────────────────────── */

export const auth = {
  // Captcha artık body'de DEĞİL — Kaynak paterni: CAPTCHA_REQUIRED yanıtını
  // global interceptor yakalar, X-Captcha-Token/X-Captcha-Type başlıklarıyla retry eder.
  login: (login: string, password: string, rememberMe: boolean = false) =>
    apiFetch<any>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ login, password, rememberMe }),
    }),

  // 7.1.3.6 — Login MFA flow.
  // /auth/login response'u { mfa: true, ticket, methods } dönerse caller bu
  // endpoint'leri tetikler. Başarı response'u normal login ile aynı shape:
  // { accessToken, refreshToken, expiresIn, profile }.
  loginMfaTotp: (ticket: string, code: string) =>
    apiFetch<any>("/auth/login/mfa/totp", {
      method: "POST",
      body: JSON.stringify({ ticket, code }),
    }),

  loginMfaSmsSend: (ticket: string) =>
    apiFetch<{ message: string }>("/auth/login/mfa/sms/send", {
      method: "POST",
      body: JSON.stringify({ ticket }),
    }),

  loginMfaSms: (ticket: string, code: string) =>
    apiFetch<any>("/auth/login/mfa/sms", {
      method: "POST",
      body: JSON.stringify({ ticket, code }),
    }),

  loginMfaBackupCode: (ticket: string, code: string) =>
    apiFetch<any>("/auth/login/mfa/backup-code", {
      method: "POST",
      body: JSON.stringify({ ticket, code }),
    }),

  // AUTH-DENETİM D1 (2026-07-18) — Kaynak WebAuthn MFA (MfaScreen passkey
  // seçeneği). options → navigator.credentials.get() → complete; payload
  // şekli backend MfaWebAuthnRequest (passkeysAssert ile aynı alan adları).
  loginMfaWebAuthnOptions: (ticket: string) =>
    apiFetch<{ challenge: string; rpId: string; allowCredentialIds: string[] }>(
      "/auth/login/mfa/webauthn/authentication-options",
      { method: "POST", body: JSON.stringify({ ticket }) },
    ),
  // ŞİFRESİZ PASSKEY GİRİŞİ (2026-07-18) — Kaynak AuthenticationCommands.
  // getWebAuthnAuthenticationOptions / authenticateWithWebAuthn (oturumsuz,
  // discoverable/usernameless; SignInPage passkey butonu). options →
  // navigator.credentials.get() (allowCredentials YOK, userVerification:
  // 'required') → authenticate; başarı yanıtı /auth/login ile AYNI şekil
  // ({ token, profile }).
  webauthnAuthenticationOptions: () =>
    apiFetch<{ challenge: string; rpId: string; userVerification: string }>(
      "/auth/webauthn/authentication-options",
      { method: "POST", body: "{}" },
    ),
  webauthnAuthenticate: (payload: {
    challenge: string;
    credentialId: string;
    clientDataJson: string;
    authenticatorData: string;
    signature: string;
  }) =>
    apiFetch<any>("/auth/webauthn/authenticate", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  loginMfaWebAuthn: (payload: {
    ticket: string;
    credentialId: string;
    clientDataJson: string;
    authenticatorData: string;
    signature: string;
  }) =>
    apiFetch<any>("/auth/login/mfa/webauthn", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  register: (data: {
    name: string;
    username: string;
    email: string;
    password: string;
    birthDate: string;
    verificationCode?: string;
  }) =>
    apiFetch<any>("/auth/register", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // username İSTEĞE BAĞLI: verilirse backend register'daki kullanıcı-adı
  // denetimlerinin aynısını modal açılmadan çalıştırır (409 → alan-altı hata).
  sendVerification: (email: string, name?: string, username?: string) =>
    apiFetch<{ message: string; expiresIn: number }>("/auth/send-verification", {
      method: "POST",
      body: JSON.stringify({ email, name, username }),
    }),

  forgotPassword: (email: string) =>
    apiFetch<{ message: string }>("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  // Sprint 9.A — Backend artık reset sonrası tüm session'ları revoke ediyor.
  // Response: { message, all_sessions_revoked: true, revoked_count: number }
  // OPAK-OTURUM hizası (2026-07-18): DTO alanı snake_case `new_password`
  // (ResetPasswordRequest, tebhos_dto/src/auth.rs) — camelCase 400 veriyordu.
  resetPassword: (token: string, newPassword: string) =>
    apiFetch<{ message: string; allSessionsRevoked?: boolean; revokedCount?: number }>("/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ token, new_password: newPassword }),
    }),

  // Sprint 27 — Kaynak güvenlik akışları: e-posta/parola değişimi e-posta
  // koduyla (start → resend → verify → complete), passkey yönetimi WebAuthn ile.
  // GÜVENLİK-PARİTE (2026-07-18): resend uçları + resend_available_at sunucu
  // değerleri + complete'in replaceCurrentAuthSession token'ı eklendi.
  passwordChangeStart: () =>
    apiFetch<{ ticket: string; codeExpiresAt: string; resendAvailableAt: string }>(
      "/auth/password-change/start",
      { method: "POST", body: "{}" },
    ),
  passwordChangeResend: (ticket: string) =>
    apiFetch<void>("/auth/password-change/resend", {
      method: "POST",
      body: JSON.stringify({ ticket }),
    }),
  passwordChangeVerify: (ticket: string, code: string) =>
    apiFetch<{ verificationProof: string }>("/auth/password-change/verify", {
      method: "POST",
      body: JSON.stringify({ ticket, code }),
    }),
  // Kaynak completePasswordChange 1-1: yanıt {token, auth_session_id_hash} —
  // MEVCUT cihaz yeni token'ı sessizce devralır (yeniden-login YOK).
  passwordChangeComplete: async (ticket: string, verificationProof: string, newPassword: string) => {
    const res = await apiFetch<{ token: string; authSessionIdHash: string }>(
      "/auth/password-change/complete",
      {
        method: "POST",
        body: JSON.stringify({ ticket, verificationProof, newPassword }),
      },
    );
    if (res?.token) setTokens(res.token);
    return res;
  },

  emailChangeStart: () =>
    apiFetch<{
      ticket: string;
      requireOriginal: boolean;
      originalEmail: string | null;
      originalProof: string | null;
      originalCodeExpiresAt: string | null;
      resendAvailableAt: string | null;
    }>("/auth/email-change/start", { method: "POST", body: "{}" }),
  emailChangeResendOriginal: (ticket: string) =>
    apiFetch<void>("/auth/email-change/resend-original", {
      method: "POST",
      body: JSON.stringify({ ticket }),
    }),
  emailChangeVerifyOriginal: (ticket: string, code: string) =>
    apiFetch<{ originalProof: string }>("/auth/email-change/verify-original", {
      method: "POST",
      body: JSON.stringify({ ticket, code }),
    }),
  emailChangeRequestNew: (ticket: string, originalProof: string, newEmail: string) =>
    apiFetch<{
      ticket: string;
      newEmail: string;
      newCodeExpiresAt: string;
      resendAvailableAt: string | null;
    }>("/auth/email-change/request-new", {
      method: "POST",
      body: JSON.stringify({ ticket, originalProof, newEmail }),
    }),
  emailChangeResendNew: (ticket: string) =>
    apiFetch<void>("/auth/email-change/resend-new", {
      method: "POST",
      body: JSON.stringify({ ticket }),
    }),
  emailChangeComplete: (ticket: string, originalProof: string, code: string) =>
    apiFetch<{ message: string; email: string }>("/auth/email-change/complete", {
      method: "POST",
      body: JSON.stringify({ ticket, originalProof, code }),
    }),

  // GÜVENLİK-PARİTE (2026-07-18): last_used_at Kaynak listWebAuthnCredentials 1-1.
  passkeysList: () =>
    apiFetch<{ passkeys: { id: string; name: string; createdAt: string; lastUsedAt: string | null }[] }>(
      "/auth/passkeys",
    ),
  // GÜVENLİK (2026-09-21): register-options / rename / delete artık gövdede
  // sudo alanı ister (kaynak-referans WebAuthn uçları sudo'lu) — ele geçirilmiş
  // oturum kendi anahtarını ekleyip hesabı kilitleyemesin. Alanlar ortak sudo
  // akışından (runWithSudo) gelir; ilk deneme alansızdır.
  passkeysRegisterOptions: (sudo: SudoBody = {}) =>
    apiFetch<{
      challenge: string;
      rpId: string;
      rpName: string;
      userId: string;
      userName: string;
      userDisplayName: string;
      excludeCredentialIds: string[];
    }>("/auth/passkeys/register-options", { method: "POST", body: JSON.stringify({ ...sudo }) }),
  passkeysRegister: (payload: {
    name: string;
    credentialId: string;
    clientDataJson: string;
    attestationObject: string;
  }) =>
    apiFetch<{ id: string; name: string; createdAt: string }>("/auth/passkeys/register", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  // GÜVENLİK-PARİTE (2026-07-18) — Kaynak list_sudo_mfa_methods 1-1:
  // Sudo doğrulama modalı hangi yöntemleri göstereceğini buradan öğrenir
  // (password yalnız has_mfa=false iken).
  sudoMfaMethods: () =>
    apiFetch<{ totp: boolean; webauthn: boolean; hasMfa: boolean }>("/auth/sudo/mfa-methods"),

  // Sprint 27 — Passkey ile sudo (kaynak "Güvenlik anahtarı kullan"):
  // assert-options → navigator.credentials.get() → assert → sudo_proof.
  passkeysAssertOptions: () =>
    apiFetch<{ challenge: string; rpId: string; allowCredentialIds: string[] }>(
      "/auth/passkeys/assert-options",
      { method: "POST", body: "{}" },
    ),
  passkeysAssert: (payload: {
    credentialId: string;
    clientDataJson: string;
    authenticatorData: string;
    signature: string;
  }) =>
    apiFetch<{ sudoProof: string }>("/auth/passkeys/assert", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  passkeysRename: (id: string, name: string, sudo: SudoBody = {}) =>
    apiFetch<{ message: string }>(`/auth/passkeys/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ name, ...sudo }),
    }),
  passkeysDelete: (id: string, sudo: SudoBody = {}) =>
    apiFetch<{ message: string }>(`/auth/passkeys/${id}`, { method: "DELETE", body: JSON.stringify({ ...sudo }) }),

  resendVerification: (email: string) =>
    apiFetch<{ message: string; expiresIn: number }>("/auth/send-verification", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  // 7.1.3.3 — SMS 2FA (yeni Backend Architecture pattern).
  // apiFetch otomatik camelCase -> snake_case body conversion yapar.
  enrollSms2fa: (phoneNumber: string) =>
    apiFetch<{ phoneNumberMasked: string; ttlSeconds: number }>("/auth/2fa/sms/enroll", {
      method: "POST",
      body: JSON.stringify({ phoneNumber }),
    }),

  verifySms2fa: (code: string) =>
    apiFetch<{ message: string }>("/auth/2fa/sms/verify", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),

  resendSms2fa: () =>
    apiFetch<{ phoneNumberMasked: string; ttlSeconds: number }>("/auth/2fa/sms/resend", { method: "POST" }),

  // 7.1.3.4.A — TOTP 2FA (Authenticator app, RFC 6238).
  // setup secret/qr_url döner. verify ilk kodu doğrular ve aktive eder.
  setupTotp2fa: () => apiFetch<{ qrUrl: string; secretBase32: string }>("/auth/2fa/totp/setup", { method: "POST" }),

  // GÜVENLİK (2026-09-21): verify sudo alanı ister ve İLK yedek kodları BU
  // yanıtla döndürür (kaynak-referans enableMfaTotp) — ayrıca regenerate çağrılmaz.
  verifyTotp2fa: (code: string, sudo: SudoBody = {}) =>
    apiFetch<{ message: string; backupCodes?: { code: string; consumed: boolean }[] }>("/auth/2fa/totp/verify", {
      method: "POST",
      body: JSON.stringify({ code, ...sudo }),
    }),

  // GÜVENLİK-PARİTE (2026-07-18) — Kaynak disableMfaTotp 1-1: kod ZORUNLU
  // (authenticator kodu VEYA yedek kod); Kaynak-path uç, 204 döner.
  disableTotp2fa: (code: string) =>
    apiFetch<void>("/users/@me/mfa/totp/disable", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),

  // 7.1.3.4.B — Backup codes (account recovery).
  // regenerate: düz metin kod döndürdüğü için sudo alanı ZORUNLU. TOTP kurulum
  // zinciri artık bunu çağırmaz (kodlar verify yanıtında). Görüntüleme için
  // getMfaBackupCodes (Kaynak getBackupCodes 1-1, sudo'lu).
  regenerateBackupCodes: (sudo: SudoBody = {}) =>
    apiFetch<{ codes: string[]; generatedAt: string }>("/auth/2fa/backup-codes/regenerate", {
      method: "POST",
      body: JSON.stringify({ ...sudo }),
    }),

  // GÜVENLİK-PARİTE (2026-07-18) — Kaynak MfaCommands.getBackupCodes 1-1:
  // {regenerate:false} MEVCUT kodları consumed işaretiyle döndürür; sudo
  // alanları 403 SUDO_MODE_REQUIRED retry'ında ortak sudo akışınca eklenir.
  getMfaBackupCodes: (regenerate: boolean, sudo: SudoBody = {}) =>
    apiFetch<{ backupCodes: { code: string; consumed: boolean }[] }>("/users/@me/mfa/backup-codes", {
      method: "POST",
      body: JSON.stringify({ regenerate, ...sudo }),
    }),

  listBackupCodes: () => apiFetch<{ activeCount: number; totalCount: number }>("/auth/2fa/backup-codes/list"),

  // 7.1.3.5 — Account lifecycle.
  // GÜVENLİK-PARİTE (2026-07-18) — Kaynak disable/delete sudo-tabanlı
  // (şifre-alanı yok; kimlik kanıtı transport-sudo — 403'te ortak sudo akışı
  // retry'da alanı ekler). Başarıda 204; backend tüm oturumları düşürür.
  disableAccount: (sudo: SudoBody = {}) =>
    apiFetch<void>("/auth/account/disable", { method: "POST", body: JSON.stringify({ ...sudo }) }),

  deleteAccount: (sudo: SudoBody = {}) =>
    apiFetch<void>("/auth/account/delete", { method: "POST", body: JSON.stringify({ ...sudo }) }),

  verifyResetToken: (token: string) =>
    apiFetch<{ valid: boolean; expiresIn: number }>(`/auth/reset-password/verify?token=${encodeURIComponent(token)}`),

  // AUTH-DENETİM O4 (2026-07-18) — Kaynak AuthenticationCommands
  // revertEmailChange/authorizeIp karşılıkları (backend parity_controller
  // uçları zaten vardı, FE sayfaları yeni bağlandı).
  // email-revert: eski adrese giden "bu ben değildim" linki — başarıda TEBHOS
  // auth-yanıtı (accessToken/refreshToken/profile) döner, sayfa oturumu açar.
  emailRevert: (token: string, password: string) =>
    apiFetch<any>("/auth/email-revert", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    }),
  // authorize-ip: yeni-konum onay linki — 204 döner (oturum, bekleyen login
  // ekranının poll ucuna yazılır; bu cihazda oturum AÇILMAZ — Kaynak 1-1).
  authorizeIp: (token: string) =>
    apiFetch<Record<string, never>>("/auth/authorize-ip", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),
  // DALGA-2 (2026-07-18) — Kaynak resendIpAuthorization/pollIpAuthorization
  // (AuthenticationCommands.ts:504-524): bekleyen login ekranının (SignInPage
  // IpAuthorizationScreen) yeniden-gönder + 2sn polling uçları.
  resendIpAuthorization: (ticket: string) =>
    apiFetch<Record<string, never>>("/auth/ip-authorization/resend", {
      method: "POST",
      body: JSON.stringify({ ticket }),
    }),
  pollIpAuthorization: (ticket: string) =>
    apiFetch<{ completed: boolean; token?: string; userId?: string; user?: any }>(
      `/auth/ip-authorization/poll?ticket=${encodeURIComponent(ticket)}`,
    ),
  // DALGA-2 (2026-07-18) — Kaynak resendIpAuthorization/pollIpAuthorization
  // (AuthenticationCommands.ts:504-524): bekleyen login ekranının (SignInPage
  // IpAuthorizationScreen) yeniden-gönder + 2sn polling uçları.
  resendIpAuthorization: (ticket: string) =>
    apiFetch<Record<string, never>>("/auth/ip-authorization/resend", {
      method: "POST",
      body: JSON.stringify({ ticket }),
    }),
  pollIpAuthorization: (ticket: string) =>
    apiFetch<{ completed: boolean; token?: string; userId?: string; user?: any }>(
      `/auth/ip-authorization/poll?ticket=${encodeURIComponent(ticket)}`,
    ),

  me: () => apiFetch<any>("/auth/me"),

  // OPAK-OTURUM (2026-07-18) — `refresh` SÖKÜLDÜ (backend ucu da kalktı;
  // Kaynak'da refresh yok). Çıkış: Kaynak logout_user 1-1 — yalnız mevcut
  // oturumun satırı silinir (sunucuda anında iptal).
  logout: () => apiFetch<Record<string, never>>("/auth/logout", { method: "POST" }),

  checkUsername: (username: string) =>
    apiFetch<{ available: boolean; reason: string }>("/auth/check-username", {
      method: "POST",
      body: JSON.stringify({ username }),
    }),

  checkEmail: (email: string) =>
    apiFetch<{ available: boolean; reason: string }>("/auth/check-email", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  // Kaynak AUTH_USERNAME_SUGGESTIONS paritesi — görünen isimden kullanıcı-adı
  // önerisi ("Ne dersin: ..." satırı). Auth gerektirmez, rate-limit'li.
  usernameSuggestions: (globalName: string) =>
    apiFetch<{ suggestions: Array<string> }>("/auth/username-suggestions", {
      method: "POST",
      body: JSON.stringify({ global_name: globalName }),
    }),
};

/* ── ApiError class ────────────────────────────────────────────────── */

export class ApiError extends Error {
  status: number;
  body: any;
  constructor(message: string, status: number, body?: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

/* ── Server endpoints ──────────────────────────────────────────────── */

// Rust API returns flat {id, name, imageUrl, role, profileId, ...}
// Frontend expects nested {profile: {id, name, imageUrl}, ...}
function mapServerMemberRow(m: any) {
  return {
    ...m,
    profile: m.profile || {
      id: m.profileId || m.profile_id || m.id,
      userId: m.profileId || m.profile_id || m.id,
      name: m.name || "User",
      imageUrl: m.imageUrl || m.image_url || "",
      activeFrameUrl: m.activeFrameUrl || m.active_frame_url || null,
      email: m.email || "",
      bio: null,
      customStatus: m.customStatus || null,
      bannerColor: m.bannerColor || "#5865f2",
      // BOT-rozeti (07-23): backend member DTO'su bot/system gönderir;
      // sentetik profile'a taşınmazsa MembersPanel rozeti hiç çizemez.
      bot: m.bot ?? false,
      system: m.system ?? false,
    },
    memberRoles: m.customRoles || m.memberRoles || [],
  };
}

export const servers = {
  list: () => apiFetch("/servers"),
  get: (id: string) => apiFetch(`/servers/${id}`),
  create: (data: { name: string; imageUrl?: string }) =>
    apiFetch("/servers", { method: "POST", body: JSON.stringify(data) }),
  update: (id: string, data: Record<string, any>) =>
    apiFetch(`/servers/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  delete: (id: string) => apiFetch(`/servers/${id}`, { method: "DELETE" }),
  regenerateInvite: (id: string) => apiFetch(`/servers/${id}/invite-code`, { method: "PATCH" }),
  leave: (id: string, opts?: { deleteMessages?: boolean }) =>
    apiFetch(`/servers/${id}/leave${opts?.deleteMessages ? "?delete_messages=true" : ""}`, { method: "PATCH" }),
  /**
   * Feature 5 — Sunucu sahipliğini başka bir üyeye devret.
   * Sadece mevcut owner çağırabilir. Hedef üye sunucuda olmalı.
   * Backend ADMIN rolüne otomatik yükseltir + WS server:ownership-transferred broadcast.
   */
  transferOwnership: (id: string, targetProfileId: string) =>
    apiFetch<{ ok: true; newOwnerProfileId: string }>(`/servers/${id}/transfer`, {
      method: "POST",
      body: JSON.stringify({ targetProfileId }),
    }),
  updateSettings: (id: string, data: Record<string, any>) =>
    apiFetch(`/servers/${id}/settings`, { method: "PATCH", body: JSON.stringify(data) }),
  // Sprint 28 (mirco) - global boost durumu (rozet kademeleri icin).
  myBoostStatus: () =>
    apiFetch<{
      isBoosting: boolean;
      boostingSince: string | null;
      boostCount: number;
      /**
       * TAKVİYE ROZETİ (2026-07-26) — kademe kilidinin baktığı KREDİLENDİRİLMİŞ
       * gün. Backend `boost::service::boost_credited_days`: geçen gün + aktif
       * 30 günlük dönem, yani "1 aylık takviye alan" kişi 1 Ay rozetini HEMEN
       * kullanabilir (30 gün beklemez). `boostingSince` HAM tarih olarak kalır.
       * NOT: backend bu alanı henüz döndürmüyorsa `undefined` gelir — çağıran
       * taraf (BadgesModal → `creditedDays`) bu durumda ham süreye düşer.
       */
      boostDays?: number;
      /** Sınırsız → `null`. Eski alanlar geriye-uyum için hâlâ dönüyor. */
      boostSlots?: number | null;
      boostSlotsUsed?: number;
      boostSlotsRemaining?: number | null;
      isPremium?: boolean;
      /** Takviye birim fiyatı — kuruş (4999 = 49,99 ₺). */
      boostUnitPriceMinor?: number;
      boostCurrency?: string;
      /** Polar checkout'unun tahsil ettiği USD karşılığı — cent (299 = $2.99). */
      boostUnitPriceUsdMinor?: number;
      /** GERÇEK-ÖDEME (2026-08-02): TEBHOS Pro'nun TOPLAM ücretsiz takviye
       *  hakkı (10) ve kalıcı sayaçtan kalan hak. Süresi dolan takviye hakkı
       *  GERİ GETİRMEZ. */
      freeBoostAllowance?: number;
      freeBoostsUsed?: number;
      freeBoostRemaining?: number;
    }>("/users/@me/boost-status").catch(() => null),
  getBoost: (id: string) => apiFetch(`/servers/${id}/boost`).catch(() => null),
  /** GERÇEK-ÖDEME (2026-08-02) + ÇOKLU-ADET (2026-08-04): `count` ile N
   *  takviye TEK istekte alınır. Ücretsiz hakla karşılanan kısım anında
   *  uygulanır (201 `{free:true, applied}`); ücretli kısım kaldıysa 200
   *  `{paymentRequired:true, url, freeApplied, paidCount, priceMinor}` —
   *  checkout TEK ödemede TOPLAM tutarı tahsil eder (takviyeler `order.paid`
   *  webhook'unda uygulanır). `currency` verilmezse backend ülkeye göre seçer
   *  (TR → ₺, diğerleri → $; 12 Eyl 2026 — eskiden herkese ₺ zorlanıyordu). */
  boost: (id: string, opts?: { count?: number; currency?: "try" | "usd" }) =>
    apiFetch<{
      id?: string;
      free?: boolean;
      applied?: number;
      priceMinor?: number;
      unitPriceMinor?: number;
      currency?: string;
      freeBoostRemaining?: number;
      paymentRequired?: boolean;
      freeApplied?: number;
      paidCount?: number;
      url?: string;
    }>(`/servers/${id}/boost`, {
      method: "POST",
      body: JSON.stringify({ count: opts?.count ?? 1, ...(opts?.currency ? { currency: opts.currency } : {}) }),
    }),
  unboost: (id: string) => apiFetch(`/servers/${id}/boost`, { method: "DELETE" }),
  getMembers: async (id: string) => {
    const data = await apiFetch<any[]>(`/servers/${id}/members`);
    const members = (Array.isArray(data) ? data : []).map(mapServerMemberRow);
    return { members, myRole: null };
  },
  // Sayfalı üye okuma: API varsayılanı 100, tavanı 200 satır (member/controller.rs
  // `limit`/`offset`) ve toplamı `X-Total-Count` başlığında verir. Tek çağrılık
  // `getMembers` 100'de kesiliyordu; ortak yükleyici (GuildRosterLoader) bununla sayfalar.
  getMembersPage: async (id: string, limit: number, offset: number) => {
    const { data, headers } = await apiFetchRaw<any[]>(`/servers/${id}/members?limit=${limit}&offset=${offset}`);
    const rows = Array.isArray(data) ? data : [];
    const totalHeader = headers.get("X-Total-Count");
    const total = totalHeader === null || totalHeader === "" ? Number.NaN : Number(totalHeader);
    return {
      members: rows.map(mapServerMemberRow),
      rawCount: rows.length,
      total: Number.isFinite(total) && total >= 0 ? total : null,
    };
  },
  getBans: (id: string) => apiFetch(`/servers/${id}/bans`),
  createBan: (id: string, data: Record<string, any>) =>
    apiFetch(`/servers/${id}/bans`, { method: "POST", body: JSON.stringify(data) }),
  removeBan: (id: string, banId: string) => apiFetch(`/servers/${id}/bans/${banId}`, { method: "DELETE" }),
  getAuditLog: (id: string, params?: string) => apiFetch(`/servers/${id}/audit-log${params ? `?${params}` : ""}`),
  getInvites: (id: string) => apiFetch(`/servers/${id}/invites`),
  // Sprint 24 — Kaynak "Davetleri Duraklat": true iken davetle katılım engellenir.
  setInvitesDisabled: (id: string, disabled: boolean) =>
    apiFetch(`/servers/${id}/invites/disabled`, { method: "PATCH", body: JSON.stringify({ disabled }) }),
  createInvite: (id: string, data: Record<string, any>) =>
    apiFetch(`/servers/${id}/invites`, { method: "POST", body: JSON.stringify(data) }),
  deleteInvite: (id: string, inviteId: string) => apiFetch(`/servers/${id}/invites/${inviteId}`, { method: "DELETE" }),
  getRoles: (id: string) => apiFetch(`/servers/${id}/roles`),
  createRole: (id: string, data: Record<string, any>) =>
    apiFetch(`/servers/${id}/roles`, { method: "POST", body: JSON.stringify(data) }),
  updateRole: (id: string, roleId: string, data: Record<string, any>) =>
    apiFetch(`/servers/${id}/roles/${roleId}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteRole: (id: string, roleId: string) => apiFetch(`/servers/${id}/roles/${roleId}`, { method: "DELETE" }),
  assignMemberRole: (id: string, memberId: string, roleId: string) =>
    apiFetch(`/servers/${id}/members/${memberId}/roles`, { method: "POST", body: JSON.stringify({ roleId }) }),
  removeMemberRole: (id: string, memberId: string, roleId: string) =>
    apiFetch(`/servers/${id}/members/${memberId}/roles/${roleId}`, { method: "DELETE" }),
  // Sprint 25 — "Sunucu Başına Profili Düzenle" self-update.
  // Sprint 27 — per-guild profil alanları (kaynak guild profile 1-1).
  // DİKKAT semantik: alan GÖNDERİLMEZSE dokunulmaz; "" = temizle/mirasa dön
  // (nickname dahil — null artık "dokunma" demek, reset için "" gönder).
  // nickname change_nickname izni; diğer alanlar premium ister (backend gate).
  updateOwnMember: (
    id: string,
    body: {
      nickname?: string;
      /** "" = global'i miras al; URL = özel per-guild avatar. */
      avatarUrl?: string;
      /** true = default avatara sabitle (kaynak AVATAR_UNSET). */
      avatarUnset?: boolean;
      /** "" = miras; URL = özel banner. */
      bannerUrl?: string;
      /** true = banner GÖSTERME (accent'e düş). */
      bannerUnset?: boolean;
      bio?: string;
      pronouns?: string;
      accentColor?: number;
      /** true = accent override'ını kaldır (global'e dön). */
      accentClear?: boolean;
    },
  ) => apiFetch(`/servers/${id}/members/@me`, { method: "PATCH", body: JSON.stringify(body) }),
  // Kaynak/Discord-spec MANAGE_NICKNAMES — başka üyenin sunucu nick'ini değiştir
  // (herkese görünür). YORUM-FIX (2026-08-30): URL'deki {member_id} backend W-C
  // kuralıyla PROFİL SNOWFLAKE'idir (MemberResponse.id = user_id) — eski
  // "members.id (profile değil)" ifadesi bayattı ve kimlik-karışıklığı bug
  // sınıfını yeniden üretebilirdi. Boş/null → reset.
  setMemberNickname: (serverId: string, memberId: string, nickname: string | null) =>
    apiFetch(`/servers/${serverId}/members/${memberId}/nickname`, {
      method: "PATCH",
      body: JSON.stringify({ nickname }),
    }),
  timeoutMember: (id: string, memberId: string, data: Record<string, any>) =>
    apiFetch(`/servers/${id}/members/${memberId}/timeout`, { method: "POST", body: JSON.stringify(data) }),
  // 7.4.1 — Custom server stickers (guild_sticker domain).
  // Backend: GET/POST /servers/{id}/stickers + PATCH/DELETE /servers/{id}/stickers/{stickerId}
  // Limit: 5 free / 30 premium (user-level Premium, not boost).
  stickers: {
    list: (serverId: string) => apiFetch<{ items: Sticker[] }>(`/servers/${serverId}/stickers`),
    create: (serverId: string, data: { name: string; description?: string; imageUrl: string; tags: string[] }) =>
      apiFetch<{ id: string; name: string }>(`/servers/${serverId}/stickers`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (serverId: string, stickerId: string, data: { name?: string; description?: string; tags?: string[] }) =>
      apiFetch<{ ok: true }>(`/servers/${serverId}/stickers/${stickerId}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    delete: (serverId: string, stickerId: string) =>
      apiFetch<{ ok: true }>(`/servers/${serverId}/stickers/${stickerId}`, { method: "DELETE" }),
  },
};

/** GuildSticker — server's custom sticker. */
export interface Sticker {
  id: string;
  guildId: string;
  name: string;
  description: string | null;
  imageUrl: string;
  tags: string[];
  createdBy: string;
  createdAt: string;
}

/* ── DM channel endpoints (Sprint 17.D polymorphic) ────────────────── */
//
// Backend Sprint 17.A polymorphic foundation:
//   POST /users/@me/channels                — DM veya Group DM oluştur
//   GET  /users/@me/channels                — kullanıcının tüm DM kanal listesi
//   PUT  /channels/{id}/recipients/{uid}    — Group DM'e ekle
//   DELETE /channels/{id}/recipients/{uid}  — çıkar / leave
//
// channel.type ENUM: 'DM' (1-on-1, owner_id NULL) | 'GROUP_DM' (2-10 user, owner_id set)
// recipient_ids: self HARİÇ. Backend self'i otomatik recipient_ids'e ekler.

export interface DMRecipient {
  id: string;
  name: string;
  imageUrl: string;
}

export interface DMChannel {
  id: string;
  // GET listesinde metin ("DM"/"GROUP_DM"); POST /users/@me/channels yanıtı
  // kaynak-referans biçiminde sayısal ChannelType (1/3) taşır. Domain tarafı
  // tipi `normalizeChannelTypeCode(channelType ?? type)` ile okur.
  type: "DM" | "GROUP_DM" | number;
  // Kaynak numeric ChannelType (i32) — backend `ChannelResponse.channel_type`.
  channelType?: number;
  channel_type?: number;
  name: string;
  ownerId: string | null;
  icon: string | null;
  recipients: DMRecipient[];
  /** Kaynak `last_message_id` (snowflake string) — DM sıralama girdisi. */
  lastMessageId?: string | null;
  last_message_id?: string | null;
}

export const dmChannels = {
  // Liste kaynak-referans biçiminde düz dizi; eski `{items}` zarfı da kabul edilir.
  list: async (): Promise<{ items: DMChannel[] }> => {
    const data = await apiFetch<DMChannel[] | { items: DMChannel[] }>("/users/@me/channels");
    return Array.isArray(data) ? { items: data } : { items: data?.items ?? [] };
  },

  // 1 recipient → 1-on-1 DM (idempotent — varsa aynı channel'ı döner).
  // 2-9 recipient → Group DM (max 10 user dahil; self otomatik eklenir).
  // name yalnızca Group DM için anlamlı; 1-on-1'de göz ardı edilir.
  create: (recipientIds: string[], name?: string): Promise<DMChannel> =>
    apiFetch<DMChannel>("/users/@me/channels", {
      method: "POST",
      body: JSON.stringify({ recipientIds, name }),
    }),

  // Group DM yönetimi
  addRecipient: (channelId: string, userId: string) =>
    apiFetch<void>(`/channels/${channelId}/recipients/${userId}`, { method: "PUT" }),

  removeRecipient: (channelId: string, userId: string) =>
    apiFetch<void>(`/channels/${channelId}/recipients/${userId}`, { method: "DELETE" }),
};

/* ── Channel endpoints ─────────────────────────────────────────────── */

export const channels = {
  // Sprint 11.A: extended payload — slow_mode/is_nsfw/topic/category_id desteklendi.
  create: (
    serverId: string,
    data: {
      name: string;
      type: string;
      category?: string;
      categoryId?: string;
      topic?: string;
      slowMode?: number;
      isNsfw?: boolean;
      isPrivate?: boolean;
    }
  ) => apiFetch(`/channels`, { method: "POST", body: JSON.stringify({ ...data, server_id: serverId }) }),
  get: (id: string, serverId: string) => apiFetch(`/channels/${id}?server_id=${serverId}`),
  update: (id: string, serverId: string, data: Record<string, any>) =>
    apiFetch(`/channels/${id}?server_id=${serverId}`, { method: "PATCH", body: JSON.stringify(data) }),
  delete: (id: string, serverId: string) => apiFetch(`/channels/${id}?server_id=${serverId}`, { method: "DELETE" }),
  toggleLock: (id: string, serverId: string, data: { locked: boolean }) =>
    apiFetch(`/channels/${id}/lock?server_id=${serverId}`, { method: "PATCH", body: JSON.stringify(data) }),
  reorder: (serverId: string, channelIds: string[]) =>
    apiFetch(`/servers/${serverId}/channels/reorder`, { method: "PATCH", body: JSON.stringify({ channelIds }) }),
};

/* ── Sprint 20.A/B: Notification Settings + Push Subscription ─────── */
//
// Sprint 20.A backend endpoint'leri (notification/ domain extension):
//   PUT    /users/@me/push-subscription  { endpoint, keys: {p256dh, auth} }
//   DELETE /users/@me/push-subscription  { endpoint }
//   GET    /users/@me/notification-settings
//   PATCH  /users/@me/notification-settings  { server_id?, channel_id?, level, muted_until? }
//   PATCH  /users/@me/email-digest          { enabled }
//
// Level ENUM (Discord-spec, Kaynak parite):
//   0 = ALL_MESSAGES   (default DM)
//   1 = ONLY_MENTIONS  (default server)
//   2 = NOTHING        (mute)

export const notifications = {
  /** Sprint 7.0 legacy — unread count fetch (NotificationCenter store reactive). */
  list: () => apiFetch<{ channels: any[]; dms: any[] }>("/notifications"),
  // Sprint 24.A — conversationId opsiyonel parametre SİLİNDİ (Sprint 23'te backend
  // MarkReadRequest.conversation_id field kaldırıldı; FE bu paramı hiçbir zaman
  // göndermiyordu — 1 caller channelId-only kullanıyor).
  markRead: (data: { channelId?: string }) =>
    apiFetch("/notifications/read", { method: "POST", body: JSON.stringify(data) }),

  /** Sprint 20.A — Push subscription persist (subscribe sonrası). */
  setPushSubscription: (endpoint: string, p256dh: string, auth: string) =>
    apiFetch("/users/@me/push-subscription", {
      method: "PUT",
      body: JSON.stringify({ endpoint, keys: { p256dh, auth } }),
    }),
  removePushSubscription: (endpoint: string) =>
    apiFetch("/users/@me/push-subscription", {
      method: "DELETE",
      body: JSON.stringify({ endpoint }),
    }),

  /** Sprint 20.A — Per-server / per-channel mute settings. */
  getSettings: () =>
    apiFetch<{
      items: Array<{
        server_id?: string;
        channel_id?: string;
        level: number;
        muted_until: string | null;
        updated_at: string;
      }>;
    }>("/users/@me/notification-settings"),

  /** Sprint 20.A — server_id XOR channel_id (sadece biri). */
  updateSettings: (data: { serverId?: string; channelId?: string; level: 0 | 1 | 2; mutedUntil?: string | null }) =>
    apiFetch("/users/@me/notification-settings", {
      method: "PATCH",
      body: JSON.stringify({
        server_id: data.serverId,
        channel_id: data.channelId,
        level: data.level,
        muted_until: data.mutedUntil ?? null,
      }),
    }),

  /** Sprint 20.A — Email digest opt-out toggle. */
  toggleEmailDigest: (enabled: boolean) =>
    apiFetch<{ enabled: boolean }>("/users/@me/email-digest", {
      method: "PATCH",
      body: JSON.stringify({ enabled }),
    }),
};

// Sprint 11.A — Category CRUD (Kaynak parite entity).
export const categories = {
  create: (serverId: string, data: { name: string; position?: number }) =>
    apiFetch(`/categories`, { method: "POST", body: JSON.stringify({ ...data, server_id: serverId }) }),
  update: (id: string, data: { name?: string; position?: number; isCollapsedDefault?: boolean }) =>
    apiFetch(`/categories/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  delete: (id: string) => apiFetch(`/categories/${id}`, { method: "DELETE" }),
  reorder: (categories: Array<{ id: string; position: number }>) =>
    apiFetch(`/categories/reorder`, { method: "PATCH", body: JSON.stringify({ categories }) }),
};

/* ── Message endpoints ─────────────────────────────────────────────── */

// kaynak-referans wire-migration — `?format=tebhos` ekle (url zaten query içerebilir,
// örn. `/messages?channelId=ch-1`). Bug fix: create/update HTTP yanıtı önceden
// native `MessageResponse` (author `{id,name,image_url}`) döndürüyordu; FE artık
// kaynak-referans author şekli (`username/global_name/avatar`) beklediği için optimistic
// mesajın author'ı üzerine yazılınca gönderenin adı sayfa yenilenene kadar boş
// kalıyordu (bkz. MessageActionCreators.sendMessage/editMessage + MessageQueue —
// hepsi bu `create`/`update` sarmalayıcısından geçer).
function withTebhosFormat(url: string): string {
  const [base, qs] = url.split("?");
  const params = new URLSearchParams(qs);
  params.set("format", "tebhos");
  return `${base}?${params.toString()}`;
}

export const messages = {
  list: (params: Record<string, string>) => {
    // kaynak-referans wire-migration — Discord-shape ApiMessageResponse (author partial +
    // timestamp/type/referenced_message). Çağıran zaten format geçmediyse ekle.
    const merged = { format: "tebhos", ...params };
    const qs = new URLSearchParams(merged).toString();
    return apiFetch(`/messages?${qs}`);
  },
  create: (url: string, data: Record<string, any>) =>
    apiFetch(withTebhosFormat(url.replace(API_BASE, "")), { method: "POST", body: JSON.stringify(data) }),
  update: (url: string, data: Record<string, any>) =>
    apiFetch(withTebhosFormat(url.replace(API_BASE, "")), { method: "PATCH", body: JSON.stringify(data) }),
  delete: (url: string) => apiFetch(url.replace(API_BASE, ""), { method: "DELETE" }),
  /**
   * Kaynak-referans paritesi — çağıranın bir kanaldaki TÜM kendi mesajlarını siler.
   * Backend: POST /channels/{channel_id}/messages/bulk-delete-mine (202; require_sudo).
   * Sudo challenge gerekirse backend 401/403 döner; çağıran hatayı toast'lar.
   */
  bulkDeleteMine: (channelId: string) =>
    apiFetch<void>(`/channels/${channelId}/messages/bulk-delete-mine`, { method: "POST" }),
  togglePin: (data: Record<string, any>) =>
    apiFetch("/pinned-messages", { method: "POST", body: JSON.stringify(data) }),
  getPinned: (params: string) => apiFetch(`/pinned-messages?${params}`),
  // Mesaj araması eski `GET /messages/search` (format=tebhos, tz_offset) sarmalayıcısından
  // kaynak-referans `POST /search/messages` çağrısına taşındı:
  // features/search/utils/SearchUtils.ts `searchMessages`.
};

/* ── Member endpoints ──────────────────────────────────────────────── */

export const members = {
  list: (serverId: string) => apiFetch(`/members?server_id=${serverId}`),
  // `updateRole` (PATCH /members/{id}?server_id=) KALDIRILDI: uç hiçbir şey yapmadan
  // başarı dönüyordu, API artık 410 Gone veriyor. Rol yönetimi: assignRole/removeRole.
  kick: (id: string, serverId: string) => apiFetch(`/servers/${serverId}/members/${id}`, { method: "DELETE" }),
  setTimeout: (id: string, serverId: string, timeout: number) =>
    apiFetch(`/members/${id}/timeout?server_id=${serverId}`, { method: "PATCH", body: JSON.stringify({ timeout }) }),
  removeTimeout: (id: string, serverId: string) =>
    apiFetch(`/members/${id}/timeout?server_id=${serverId}`, { method: "DELETE" }),
  assignRole: (id: string, serverId: string, roleId: string) =>
    apiFetch(`/members/${id}/roles?server_id=${serverId}`, { method: "POST", body: JSON.stringify({ roleId }) }),
  removeRole: (id: string, serverId: string, roleId: string) =>
    apiFetch(`/members/${id}/roles/${roleId}?server_id=${serverId}`, { method: "DELETE" }),
};

/* ── Direct message endpoints (Sprint 17.D polymorphic) ────────────── */
//
// Eski direct_message backend domain'i Sprint 17.B'de SİLİNDİ. Bu wrapper
// yeni polymorphic /users/@me/channels endpoint'lerini sarar, UI'ın eski
// shape'ini (conversation/currentMember/otherMember) korur ki yüzeysel
// frontend kodu büyük çaplı değişmesin (sprint scope: incremental migration).
//
// Yeni canlı API:
//   list()                            → /users/@me/channels         (Group DM dahil)
//   openWithProfile(targetProfileId)  → POST /users/@me/channels recipientIds=[id]
//                                       (idempotent — varsa aynı DM channel'ı döner)
//   updateSettings (close vb.)        → Backend endpoint Sprint 18+, şimdilik no-op

export const directMessages = {
  // Hedef kullanıcıya DM gönder: kanalı aç/getir (/users/@me/channels POST) +
  // mesajı yaz (/direct-messages POST). discovery/live davet paylaşımı kullanır.
  sendTo: async (targetProfileId: string, content: string) => {
    const channel = await apiFetch<{ id: string }>("/users/@me/channels", {
      method: "POST",
      body: JSON.stringify({ recipientIds: [targetProfileId] }),
    });
    return apiFetch(`/direct-messages?conversation_id=${channel.id}`, {
      method: "POST",
      body: JSON.stringify({ conversationId: channel.id, content }),
    });
  },

  conversations: async () => {
    const me = await apiFetch<any>("/auth/me").catch(() => null);
    const myId = me?.id || "";
    // Liste kaynak-referans biçiminde düz dizi (sayısal `type`, alıcılar
    // `UserPartial`: global_name/username + avatar hash); eski `{items}` zarfı
    // (TEBHOS alıcısı name/image_url) da kabul edilir.
    const data = await apiFetch<any>("/users/@me/channels");
    const rawItems: any[] = Array.isArray(data) ? data : data?.items || [];
    const needsAvatarUrl = rawItems.some((ch: any) =>
      (ch.recipients || []).some((r: any) => (r.imageUrl ?? r.image_url) == null && r.avatar),
    );
    const getUserAvatarURL = needsAvatarUrl
      ? (await import("@app/features/user/utils/AvatarUtils")).getUserAvatarURL
      : null;
    const items = rawItems.map((ch: any) => ({
      id: ch.id,
      type: ch.type,
      // Kaynak numeric ChannelType — apiFetch camelCase sonrası channel_type → channelType.
      channelType: ch.channelType ?? ch.channel_type ?? ch.type,
      name: ch.name,
      ownerId: ch.ownerId ?? ch.owner_id ?? null,
      icon: ch.icon,
      recipients: (ch.recipients || []).map((r: any) => ({
        id: r.id,
        name: r.name ?? r.globalName ?? r.global_name ?? r.username,
        imageUrl:
          r.imageUrl ??
          r.image_url ??
          (r.avatar && getUserAvatarURL ? getUserAvatarURL({ id: String(r.id), avatar: r.avatar }) : ""),
      })),
    }));
    return transformDMChannels({ items }, myId);
  },

  // Eski "memberId" tıkını korur: targetProfileId verince DM kanalı aç/getir
  // ve UI'ın beklediği conversation+otherMember shape'inde döner.
  // Sprint 17.D sonrası page artık doğrudan channelId üzerinden çalıştığı
  // için bu helper SADECE legacy `new-dm-modal` / friend "DM aç" akışları için
  // var; ileride deprecate edilebilir.
  getOrCreateConversation: async (targetProfileId: string) => {
    const channel = await apiFetch<any>("/users/@me/channels", {
      method: "POST",
      body: JSON.stringify({ recipientIds: [targetProfileId] }),
    });
    let otherProfile = { id: targetProfileId, name: "User", imageUrl: "", email: "" };
    try {
      otherProfile = await apiFetch(`/profile/${targetProfileId}`);
    } catch {}
    let myProfile = { id: "", name: "", imageUrl: "", email: "" };
    try {
      myProfile = await apiFetch("/auth/me");
    } catch {}
    return {
      conversation: {
        id: channel.id,
        status: "accepted",
        senderProfileId: myProfile.id,
      },
      currentMember: {
        id: myProfile.id,
        profileId: myProfile.id,
        profile: myProfile,
      },
      otherMember: {
        id: targetProfileId,
        profileId: targetProfileId,
        profile: otherProfile,
      },
      serverId: "home",
      memberId: targetProfileId,
      // Yeni: caller doğrudan channel kullanabilsin diye expose ediyoruz
      channelId: channel.id,
      channelType: normalizeChannelTypeCode(channel.channelType ?? channel.channel_type ?? channel.type),
      // HAM kanal yanıtı — kaynak-referans persistChannelCreate paritesi için store'a yazılır (2026-08-15, Paket 6)
      channel,
    };
  },

  // Sprint 17.B'de eski settings tablosu kaldırıldı. UI close butonu artık
  // yalnız client-side state; backend "leave DM" Sprint 18+ scope'unda.
  // No-op shim — caller success bekliyor.
  updateSettings: async (_targetProfileId: string, _data: Record<string, any>) => {
    return { ok: true };
  },

  // Mig 0062 — DM "kapat" (hide). Channel + mesajlar silinmez, sadece bu
  // user için sidebar'dan gizlenir. Yeni mesaj gelirse backend auto-unhide
  // eder; arkadaş listesinden tekrar açılırsa create_dm_channel idempotent
  // yolu unhide eder.
  close: (channelId: string) => apiFetch<void>(`/users/@me/channels/${channelId}`, { method: "DELETE" }),

  // DM sabitleme (Kaynak pin/unpin 1-1) — PUT/DELETE /users/@me/channels/{id}/pin.
  // Backend güncel sıralı pinned channel id listesini döner; UserPinnedDMStore
  // gateway `user:pinned-dms-update` ile zaten senkron olur ama optimistik
  // yanıt için de response kullanılabilir.
  pin: (channelId: string) =>
    apiFetch<{ channels: string[] }>(`/users/@me/channels/${channelId}/pin`, { method: "PUT" }),
  unpin: (channelId: string) =>
    apiFetch<{ channels: string[] }>(`/users/@me/channels/${channelId}/pin`, { method: "DELETE" }),
};

/* ── Friend endpoints SİLİNDİ (Sprint 18.B) ─────────────────────────
 *
 * Sprint 18.A `friend/` backend domain'i `relationship/` Discord-spec'e
 * dönüştü. Sprint 18.A'da 1 cycle `friends` namespace + `transformFriendsGrouped`
 * + `mapFriendEntries` backward-compat shim olarak kalmıştı. Sprint 18.B'de
 * tüm caller'lar `relationships` namespace'ine + `RelationshipStore`'a
 * migrate edildi → bu helper'lar tamamen SİL.
 */

/* ── Relationship endpoints (Sprint 18.A Discord-spec) ────────────── */
//
// Backend `friend/` domain Sprint 18.A'da SİLİNDİ; yerine `relationship/`
// (Migration 0055 + 0056). Yeni endpoint'ler Discord-spec:
//   GET    /users/@me/relationships
//   POST   /users/@me/relationships              { username }
//   PUT    /users/@me/relationships/{user_id}    (no body = accept | {type:2} = block)
//   DELETE /users/@me/relationships/{user_id}    (decline/unfriend/unblock unified)
//
// type ENUM (Kaynak parite):
//   1 = FRIEND
//   2 = BLOCKED
//   3 = INCOMING_REQUEST  (alınan)
//   4 = OUTGOING_REQUEST  (gönderilen)

export interface RelationshipRecord {
  id: string;
  type: RelationshipType;
  user: { id: string; name: string; username: string | null; imageUrl: string };
  since: string;
  nickname: string | null;
}

export const relationships = {
  list: () => apiFetch<RelationshipRecord[]>("/users/@me/relationships"),
  sendRequest: (username: string) =>
    apiFetch<RelationshipRecord>("/users/@me/relationships", {
      method: "POST",
      body: JSON.stringify({ username }),
    }),
  // KİMLİK-FIX (2026-08-30): ID ile istek gönder — kaynak-referans asıl yolu
  // (POST /relationships/{user_id}); display-name≠username 404'ünü kökten önler.
  sendRequestById: (userId: string) =>
    apiFetch<RelationshipRecord>(`/users/@me/relationships/${userId}`, { method: "POST" }),
  accept: (userId: string) => apiFetch<RelationshipRecord>(`/users/@me/relationships/${userId}`, { method: "PUT" }),
  block: (userId: string) =>
    apiFetch<RelationshipRecord>(`/users/@me/relationships/${userId}`, {
      method: "PUT",
      body: JSON.stringify({ type: RelationshipTypes.BLOCKED }),
    }),
  // unified: decline / unfriend / cancel / unblock
  remove: (userId: string) => apiFetch<void>(`/users/@me/relationships/${userId}`, { method: "DELETE" }),
};

// Sprint 19.A — `friends` namespace mini-shim TOTAL SİL.
//
// Sprint 18.A backend `friend/` → `relationship/` Discord-spec rewrite + Sprint
// 18.B WS handler rename sonrasında 11 caller hâlâ `friends as friendsApi`
// mini-shim kullanıyordu. Sprint 19.A'da tüm 11 caller (chat-welcome, textarea,
// new-dm-modal, friends-page, user-profile-card, user-context-menu,
// user-profile-modal, invite-modal, discovery/live, lib/actions/Friend*) ve
// 4-grup data fetch noktaları doğrudan `RelationshipActionCreators` veya
// `RelationshipStore` (mobx singleton) kullanır.
//
// Bu shim 1 cycle dual-stack deprecation paterninin son halkasıdır.
// `RelationshipActionCreators.listGrouped()` eski 4-grup shape'i koruyarak
// 3 sub-component'in (online-all-tab, pending-tab, blocked-tab) dokunulmasını
// önler (Karar 3.A).

/* ── Invite endpoints ──────────────────────────────────────────────── */

export const invites = {
  join: (code: string) => apiFetch(`/invites/${code}/join`, { method: "POST" }),
  preview: (code: string) => apiFetch(`/invites/preview/${code}`),
  listServer: (serverId: string) => apiFetch(`/invites?server_id=${serverId}`),
  create: (serverId: string, data?: Record<string, any>) =>
    apiFetch(`/invites?server_id=${serverId}`, { method: "POST", body: JSON.stringify(data || {}) }),
  delete: (code: string, serverId: string) => apiFetch(`/invites/${code}?server_id=${serverId}`, { method: "DELETE" }),
};

/* ── Vanity URL endpoints ──────────────────────────────────────────── */
// Sprint 5 — Custom vanity URL'leri sunucu bazında. Backend:
//   GET  /vanity/{code}/resolve     — PUBLIC, server bilgisi döner (invite_code dahil)
//   PATCH /servers/{id}/vanity-url  — manage_server izni (UI'da set)
//
// Resolve sonrası `invite_code` ile invites.join() çağrılarak gerçek join akışı
// kullanılır → vanity sadece "short URL" işlevi görüyor, ayrı join endpoint'i yok.
export const vanity = {
  resolve: (code: string) => apiFetch(`/vanity/${code}/resolve`),
  get: (serverId: string) => apiFetch(`/servers/${serverId}/vanity-url`),
  set: (serverId: string, code: string) =>
    apiFetch(`/servers/${serverId}/vanity-url`, {
      method: "PATCH",
      body: JSON.stringify({ vanity_url: code }),
    }),
  clear: (serverId: string) => apiFetch(`/servers/${serverId}/vanity-url`, { method: "DELETE" }),
};

/* ── Profile endpoints ─────────────────────────────────────────────── */

export const profile = {
  get: (id: string) => apiFetch(`/profile/${id}`),
  update: (data: Record<string, any>) => apiFetch("/profile", { method: "PATCH", body: JSON.stringify(data) }),
  card: (id: string) => apiFetch(`/profile/${id}/card`),
  me: () => apiFetch("/me"),
  // 7.1.2.2 — Username değişimi (rate-limit 3/gün backend'de).
  // Sprint 27 — Kaynak sudo paritesi: şifresiz çağrı 403 SUDO_REQUIRED döner;
  // istemci "Kimliğini Doğrula" modalını açıp şifreyle yeniden dener.
  // GÜVENLİK (2026-09-21): ortak sudo alanları (MFA'lı hesapta şifre reddedilir
  // → mfa_code/sudo_proof). Alanlar ortak sudo akışından (runWithSudo) gelir.
  changeUsername: (username: string, sudo: SudoBody = {}) =>
    apiFetch("/profile/change-username", {
      method: "PATCH",
      body: JSON.stringify({ username, ...sudo }),
    }),
};

/* ── Role endpoints ────────────────────────────────────────────────── */

// Sprint 6b temizlik: Eski `roles` API wrapper'ı orphan + URL'leri yanlıştı
// (`/roles?serverId=...` — backend pattern `/servers/{id}/roles`).
// Modern kullanım: `servers.getRoles`, `servers.createRole`, `servers.updateRole`,
// `servers.deleteRole` (yukarıda). Reorder için tabs/RolesTab/_api.ts:reorderRoles.
// Eski `roles.reorder` wrapper'ı `/roles/reorder?serverId=...` 404 dönüyordu —
// hiçbir tüketici yoktu, sessizce başarısız oluyordu.

/* ── Notification endpoints (Sprint 20.A/B konsolide edildi) ─────── */
//
// Sprint 7.0'da eklenen eski `notifications = { getAll, markRead }` namespace
// Sprint 20.B'de aşağıdaki tek `notifications` namespace'ine birleştirildi
// (yukarıdaki `/* ── Sprint 20.A/B: Notification Settings ─ */` bloğunda).
// `getAll` yerine `notifications.list()` kullanılır.

/* ── Reaction endpoints ────────────────────────────────────────────── */

export interface ReactionUser {
  id: string;
  name: string;
  imageUrl: string;
}

/**
 * Kaynak-referans paritesi: tepki ekle/kaldır isteği gateway oturum kimliğini
 * `?session_id=` ile taşır → sunucu MESSAGE_REACTION_* yayınından isteği yapan
 * oturumu hariç tutabilir (o oturum değişikliği zaten iyimser uyguladı).
 * Oturum yoksa (WS kopuk) parametre hiç eklenmez.
 */
function reactionSessionQuery(sessionId?: string | null): string {
  return sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : "";
}

export const reactions = {
  toggle: (data: Record<string, any>) => apiFetch("/reactions", { method: "POST", body: JSON.stringify(data) }),

  /**
   * Feature 2 — Discord-spec MESSAGE_REACTION_GET_USERS.
   * Tıkladığın emoji'ye reaction veren kullanıcı listesi (MessageReactionsModal için).
   *
   * Backend: GET /channels/{cid}/messages/{mid}/reactions/{emoji}
   * Permission: VIEW_CHANNEL (kanalı görebilen herkes).
   * Encoded: emoji segment URL-encoded (✅ → %E2%9C%85).
   */
  getEmojiUsers: (channelId: string, messageId: string, emoji: string) =>
    apiFetch<{ emoji: string; users: ReactionUser[] }>(
      `/channels/${channelId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`
    ),

  /**
   * Kaynak `list_reaction_users_v2` paritesi — sayfalı reaktör listesi.
   * Backend: GET /channels/{cid}/messages/{mid}/reactions/{emoji}/users?limit=&after=
   * Yanıt: { items, has_more, next_after } → apiFetch camelCase'ler
   * ({ items, hasMore, nextAfter }). Cursor `after` = önceki sayfanın nextAfter'ı.
   */
  getEmojiUsersPaged: (
    channelId: string,
    messageId: string,
    emoji: string,
    opts: { limit?: number; after?: string | null } = {}
  ) => {
    const qs = new URLSearchParams();
    if (opts.limit) qs.set("limit", String(opts.limit));
    if (opts.after) qs.set("after", opts.after);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return apiFetch<{ items: ReactionUser[]; hasMore: boolean; nextAfter: string | null }>(
      `/channels/${channelId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}/users${suffix}`
    );
  },

  /**
   * Kendi tepkini ekle — PUT .../reactions/{emoji}/@me (Discord-spec self yolu,
   * Kaynak addReaction paritesi; backend reaction/controller.rs add_self_reaction).
   * CUTOVER FİX-4A (2026-08-01): eski `toggle` (POST /reactions, Sprint-13-öncesi
   * deprecated uç) hiçbir gateway/WS olayı YAYINLAMIYOR (broadcast_reaction_delta
   * çağrısı yok — yalnız HTTP yanıtında güncel grubu döner). Reference render
   * ağacı tepki rozetini SADECE gateway MESSAGE_REACTION_ADD olayından besleniyor
   * (messaging/state/MessageReactions.ts) — bu yüzden "emoji tıklayınca hiçbir şey
   * olmuyordu". `add_self_reaction` DOĞRU broadcast_reaction_delta'yı çağırıyor.
   */
  addSelfReaction: (channelId: string, messageId: string, emoji: string, sessionId?: string | null) =>
    apiFetch<void>(
      `/channels/${channelId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}/@me${reactionSessionQuery(sessionId)}`,
      { method: "PUT" },
    ),

  /** Kendi tepkini kaldır — DELETE .../reactions/{emoji}/@me (Kaynak removeReaction self yolu). */
  removeSelfReaction: (channelId: string, messageId: string, emoji: string, sessionId?: string | null) =>
    apiFetch<void>(
      `/channels/${channelId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}/@me${reactionSessionQuery(sessionId)}`,
      { method: "DELETE" },
    ),

  /** Moderatör: bir kullanıcının tepkisini kaldır (MANAGE_MESSAGES) — DELETE .../reactions/{emoji}/{userId}. */
  removeUserReaction: (channelId: string, messageId: string, emoji: string, userId: string) =>
    apiFetch<void>(
      `/channels/${channelId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}/${userId}`,
      { method: "DELETE" }
    ),

  /** Moderatör: bir emojinin TÜM tepkilerini kaldır — DELETE .../reactions/{emoji} (Kaynak removeReactionEmoji). */
  removeEmojiReactions: (channelId: string, messageId: string, emoji: string) =>
    apiFetch<void>(`/channels/${channelId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`, {
      method: "DELETE",
    }),

  /** Moderatör: mesajdaki TÜM tepkileri kaldır — DELETE .../reactions (Kaynak removeAllReactions, MANAGE_MESSAGES). */
  removeAllReactions: (channelId: string, messageId: string) =>
    apiFetch<void>(`/channels/${channelId}/messages/${messageId}/reactions`, {
      method: "DELETE",
    }),
};

/* ── Report endpoints ──────────────────────────────────────────────── */

export const reports = {
  create: (data: Record<string, any>) => apiFetch("/reports", { method: "POST", body: JSON.stringify(data) }),
  // Sprint 25 — "Başka bir şey" foto upload: presign URL al → R2'ye direkt PUT → public URL.
  // apiFetch otomatik snake→camel çevirir: backend `upload_url` → response `uploadUrl`.
  attachmentPresign: (data: { filename: string; contentType: string; size: number }) =>
    apiFetch<{ uploadUrl: string; storageKey: string; url: string; expiresIn: number }>("/uploads/reports/url", {
      method: "POST",
      body: JSON.stringify(data),
    }),
};

/* ── Audit log endpoints ───────────────────────────────────────────── */

export const auditLog = {
  list: (serverId: string, params?: Record<string, string>) => {
    const qs = params ? `&${new URLSearchParams(params).toString()}` : "";
    return apiFetch(`/audit-log?server_id=${serverId}${qs}`);
  },
};

/* ── File endpoints ────────────────────────────────────────────────── */
//
// Sprint 17.C — `files` object SİLİNDİ. tebhos_files servisi tamamen
// kaldırıldı (Sprint 16.B deprecate cycle bitti).
//
// Yeni upload paterni (Sprint 16+):
//   - Mesaj attachment   → hooks/use-cloud-upload.ts (useCloudUpload)
//   - Avatar/banner/icon → hooks/use-asset-upload.ts (useAssetUpload)
//   - Channel file upload → hooks/use-asset-upload.ts uploadAsset
//
// Backend confirm endpoint dosya URL'lerini cdn.tebhos.com'a yazar; FILES_BASE
// prefix logic gerek yok (URL'ler artık tam HTTPS).

/* ── User endpoints ───────────────────────────────────────────────── */

export const users = {
  // PR #63 — Backend `?server_id=xxx` (snake_case, apiFetch URL query
  // auto-convert YOK — CLAUDE.md kuralı). Backend serverId verildiyse
  // member_id + custom roles + is_self döner.
  getProfileCard: (id: string, serverId?: string) =>
    apiFetch(`/profile/${id}/card${serverId ? `?server_id=${serverId}` : ""}`).catch(() => null),
  setNote: (id: string, note: string) =>
    apiFetch(`/profile/${id}/note`, { method: "POST", body: JSON.stringify({ note }) }).catch(() => null),
  setNickname: (id: string, nickname: string) =>
    apiFetch(`/profile/${id}/nickname`, { method: "POST", body: JSON.stringify({ nickname }) }).catch(() => null),
};

// Sprint P2 — Kaynak `POST /users/@me/themes` paritesi ("Share this theme"
// link akışı, `theme/components/modals/ShareThemeModal.tsx`). Backend ucu
// P1 tarafından sağlanır (tebhos_api'ye burada DOKUNULMADI); bu yalnızca FE
// istemci sarmalayıcısıdır. Yanıt `{id}` → çağıran taraf `${origin}/theme/{id}`
// linkini kurar (bkz. `AppearanceTab/ShareThemeLinkModal.tsx`).
export const themes = {
  create: (css: string) => apiFetch<{ id: string }>("/users/@me/themes", { method: "POST", body: JSON.stringify({ css }) }),
};

// Sprint 25 — `dm` namespace SİLİNDİ. Sprint 17.B legacy endpoint
// `/direct-messages/conversations/:id` zaten DROP edilmişti; FE 3 caller
// (user-context-menu, user-profile-card, user-profile-modal) Sprint 25'te
// `dmChannels.create([profileId])` polymorphic endpoint'e migrate edildi.
//
// Sprint 23 + 24.A keşif yanılgıları:
//   - Sprint 23: "dm.open 0 caller" → yanılttı (grep dar)
//   - Sprint 24.A: "2 caller bulundu" → 1 kaçırdı (user-profile-modal)
//   - Sprint 25: gerçek 3 caller migrate edildi
// Discipline #36 genişletilmiş 4-pattern aliased import grep'i resmî kural oldu.

/* ── LiveKit (Voice SFU) endpoints ─────────────────────────────────── */

export interface LiveRoomBrief {
  id: string;
  title: string;
  description: string | null;
  hostId: string;
  isActive: boolean;
  isInviteOnly: boolean;
  speakerCount: number;
  /** Backend'de DB'ye host tarafından bildirilen listener sayısı (bot
   *  simulasyonu dahil). Sonradan katılan kullanıcılar bu değeri görerek
   *  fakeListeners listesini otomatik aktive eder. */
  listenerCount: number;
  /** ODA-BANNER (fork-parite 2026-07-27): oda görselinin URL'i (yoksa null). */
  bannerUrl: string | null;
}

export const livekit = {
  getToken: (channelId: string) =>
    apiFetch<{ token: string; url: string }>("/livekit/token", {
      method: "POST",
      body: JSON.stringify({ channel_id: channelId }),
    }),

  // 17.2 — Live voice room (Clubhouse-style). Backend role validation:
  // listener default; speaker upgrade serbest, host/cohost yalnız gerçek host.
  // anonymous=true: backend metadata'da name="Anonim", avatar gizlenir.
  getLiveRoomToken: (
    roomId: string,
    role: "host" | "cohost" | "speaker" | "listener" = "listener",
    anonymous: boolean = false,
  ) =>
    apiFetch<{ token: string; url: string; room: LiveRoomBrief }>("/livekit/live-room-token", {
      method: "POST",
      body: JSON.stringify({ roomId, role, anonymous }),
    }),

  getLiveRoom: (roomId: string) =>
    apiFetch<LiveRoomBrief>(`/livekit/live-rooms/${roomId}`),

  // Yeni live room oluştur — caller otomatik host olur, gerçek UUID döner.
  // isInviteOnly=true: keşfet listesinde gizlenir, sadece link ile katılım.
  // ODA-BANNER (fork-parite 2026-07-27): bannerUrl — oluşturma sırasında
  // seçilen görselin (upload/confirm'den dönen) URL'i.
  createLiveRoom: (data: {
    title: string;
    description?: string;
    isInviteOnly?: boolean;
    bannerUrl?: string | null;
  }) =>
    apiFetch<LiveRoomBrief>("/livekit/live-rooms", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // ODA-BANNER (fork-parite 2026-07-27): oda banner'ını değiştir/kaldır —
  // sadece host. bannerUrl=null → banner kaldırılır.
  setLiveRoomBanner: (roomId: string, bannerUrl: string | null) =>
    apiFetch<{ bannerUrl: string | null }>(`/livekit/live-rooms/${roomId}/banner`, {
      method: "PATCH",
      body: JSON.stringify({ bannerUrl }),
    }),

  // Listener'ı speaker'a yükselt — sadece host. Onaylanan kullanıcı bir
  // sonraki token isteğinde "speaker" rolü alır.
  promoteToSpeaker: (roomId: string, profileId: string) =>
    apiFetch<void>(`/livekit/live-rooms/${roomId}/promote`, {
      method: "POST",
      body: JSON.stringify({ profileId }),
    }),

  // Speaker'ı listener'a düşür — sadece host. Hedef NATS event ile reload
  // olur ve listener token alır.
  demoteSpeaker: (roomId: string, profileId: string) =>
    apiFetch<void>(`/livekit/live-rooms/${roomId}/speakers/${profileId}`, {
      method: "DELETE",
    }),

  // Host bir konuşmacıyı SUNUCU seviyesinde susturur/açar (LiveKit can_publish).
  // muted=true → kullanıcı mikrofonunu açamaz; false → tekrar konuşabilir.
  setSpeakerMute: (roomId: string, profileId: string, muted: boolean) =>
    apiFetch<void>(`/livekit/live-rooms/${roomId}/mute-speaker`, {
      method: "POST",
      body: JSON.stringify({ profileId, muted }),
    }),

  // Host listener sayısını günceller (bot simulasyonu için).
  updateListenerCount: (roomId: string, count: number) =>
    apiFetch<void>(`/livekit/live-rooms/${roomId}/listener-count`, {
      method: "PATCH",
      body: JSON.stringify({ count }),
    }),

  // Aktif live room'ları listele — CANLI-ODA ALGORİTMASI (arkadaş-kopyası
  // portu 2026-07-26): scope=friends (varsayılan, yalnız arkadaşların
  // odaları) | scope=all (10'lu sayfalı) | q= (scope'tan bağımsız tüm
  // odalarda arama). Endpoint artık auth ister; yanıt
  // {rooms,total,page,perPage} şekline geçti.
  listLiveRooms: (params?: { scope?: "friends" | "all"; q?: string; page?: number }) => {
    const qs = new URLSearchParams();
    if (params?.scope) qs.set("scope", params.scope);
    if (params?.q) qs.set("q", params.q);
    if (params?.page) qs.set("page", String(params.page));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return apiFetch<LiveRoomListResponse>(`/livekit/live-rooms${suffix}`);
  },

  // Odayı kapat — sadece host yapabilir, idempotent.
  closeLiveRoom: (roomId: string) =>
    apiFetch<void>(`/livekit/live-rooms/${roomId}`, { method: "DELETE" }),

  // Host siteyi kapatırken çağrılır (pagehide). apiFetch yerine direkt fetch +
  // keepalive: true — apiFetch'in token-refresh await zinciri pagehide
  // sırasında kesilebilir, keepalive ise spec gereği unload sonrası bile
  // requesti gönderir. Token süresi geçmişse fail olur (nadir, kabul edilebilir).
  scheduleCloseLiveRoom: (roomId: string) => {
    const token = getAccessToken();
    if (!token || typeof fetch === "undefined") return Promise.resolve();
    return fetch(`${API_BASE}/livekit/live-rooms/${roomId}/schedule-close`, {
      method: "POST",
      keepalive: true,
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
  },

  cancelCloseLiveRoom: (roomId: string) =>
    apiFetch<void>(`/livekit/live-rooms/${roomId}/cancel-close`, {
      method: "POST",
    }),

  // ── Live voice room anket sistemi ────────────────────────────────────
  createPoll: (
    roomId: string,
    data: {
      question: string;
      options: string[];
      allowMultiple?: boolean;
      isAnonymous?: boolean;
      hideResults?: boolean;
      durationSec?: number | null;
    },
  ) =>
    apiFetch<PollDto>(`/voice-rooms/${roomId}/polls`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  getActivePoll: (roomId: string) =>
    apiFetch<PollDto | null>(`/voice-rooms/${roomId}/polls/active`),

  getPoll: (roomId: string, pollId: string) =>
    apiFetch<PollDto>(`/voice-rooms/${roomId}/polls/${pollId}`),

  votePoll: (roomId: string, pollId: string, optionIds: string[]) =>
    apiFetch<PollDto>(`/voice-rooms/${roomId}/polls/${pollId}/vote`, {
      method: "POST",
      body: JSON.stringify({ optionIds }),
    }),

  retractVote: (roomId: string, pollId: string) =>
    apiFetch<PollDto>(`/voice-rooms/${roomId}/polls/${pollId}/vote`, {
      method: "DELETE",
    }),

  endPoll: (roomId: string, pollId: string) =>
    apiFetch<void>(`/voice-rooms/${roomId}/polls/${pollId}/end`, {
      method: "POST",
    }),

  pollHistory: (roomId: string) =>
    apiFetch<PollDto[]>(`/voice-rooms/${roomId}/polls/history`),

  // Host'luğu başka kullanıcıya devret — sadece mevcut host yapabilir.
  transferHost: (roomId: string, newHostId: string) =>
    apiFetch<LiveRoomBrief>(`/livekit/live-rooms/${roomId}/transfer-host`, {
      method: "PATCH",
      body: JSON.stringify({ newHostId }),
    }),
};

/** Live voice room anket DTO. */
export interface PollOptionDto {
  id: string;
  text: string;
  position: number;
  voteCount: number;
}

export interface PollSettings {
  allowMultiple: boolean;
  isAnonymous: boolean;
  hideResults: boolean;
  durationSec: number | null;
}

export interface PollDto {
  id: string;
  roomId: string;
  createdById: string;
  question: string;
  status: "active" | "ended" | "cancelled";
  options: PollOptionDto[];
  settings: PollSettings;
  startsAt: string;
  endsAt: string | null;
  endedAt: string | null;
  totalVotes: number;
  myVote: string[];
}

export interface LiveRoomListItem {
  id: string;
  title: string;
  description: string | null;
  hostId: string;
  hostName: string;
  hostImageUrl: string | null;
  createdAt: string;
  speakerCount: number;
  /** Client-tracked listener (host bot simulasyonu dahil) */
  listenerCount: number;
  /** ODA-BANNER (fork-parite 2026-07-27): oda kartının orta alanındaki
   *  görsel (yoksa null). */
  bannerUrl: string | null;
}

/** CANLI-ODA ALGORİTMASI (arkadaş-kopyası portu 2026-07-26) —
 *  /livekit/live-rooms yeni yanıt şekli. apiFetch snake→camel çevirir:
 *  backend per_page → perPage. */
export interface LiveRoomListResponse {
  rooms: LiveRoomListItem[];
  total: number;
  page: number;
  perPage: number;
}

/* ── Call endpoints (Sprint 22.C — Kaynak 1:1 namespace) ──────────────
 * Sprint 22.A backend rename: /conversations/{id}/call → /channels/{id}/call.
 * Sprint 22.B'de CallActionCreators raw apiFetch wrap'liyordu — bu namespace
 * DRY: tek source-of-truth path string, body shape.
 *
 * Backend Sprint 22.A endpoint roster:
 *   GET    /channels/:id/call           → eligibility
 *   POST   /channels/:id/call           → start (response: unavailable flag)
 *   PATCH  /channels/:id/call           → update (region)
 *   DELETE /channels/:id/call           → end
 *   POST   /channels/:id/call/ring      → ring recipients
 *   POST   /channels/:id/call/stop-ringing
 *   POST   /channels/:id/call/join      → livekit token
 *   POST   /channels/:id/call/leave
 *   PATCH  /channels/:id/call/voice-state → VOICE_STATE_UPDATE (yeni)
 */

export const call = {
  checkEligibility: (channelId: string) => apiFetch(`/channels/${channelId}/call`),

  create: (channelId: string) => apiFetch(`/channels/${channelId}/call`, { method: "POST" }),

  update: (channelId: string, body: { region?: string }) =>
    apiFetch(`/channels/${channelId}/call`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  end: (channelId: string) => apiFetch(`/channels/${channelId}/call`, { method: "DELETE" }),

  join: (channelId: string) => apiFetch(`/channels/${channelId}/call/join`, { method: "POST" }),

  leave: (channelId: string) => apiFetch(`/channels/${channelId}/call/leave`, { method: "POST" }),

  ring: (channelId: string, recipients?: string[]) =>
    apiFetch(`/channels/${channelId}/call/ring`, {
      method: "POST",
      body: JSON.stringify({ recipients: recipients ?? null }),
    }),

  stopRing: (channelId: string, recipients?: string[]) =>
    apiFetch(`/channels/${channelId}/call/stop-ringing`, {
      method: "POST",
      body: JSON.stringify({ recipients: recipients ?? null }),
    }),
};

/**
 * Sprint 22.A — VOICE_STATE_UPDATE PATCH endpoint.
 * Server-authoritative mute/deaf/video/stream persist + WS broadcast.
 */
export const voiceState = {
  update: (
    channelId: string,
    body: { self_mute?: boolean; self_deaf?: boolean; self_video?: boolean; self_stream?: boolean }
  ) =>
    apiFetch<{ self_mute: boolean; self_deaf: boolean; self_video: boolean; self_stream: boolean }>(
      `/channels/${channelId}/call/voice-state`,
      { method: "PATCH", body: JSON.stringify(body) }
    ),
  // ÜYEYİ SESLİ KANALA TAŞI (22 Eyl 2026 düzeltmesi).
  //
  // Eskiden `POST /voice-states/move-member` çağrılıyordu; o uç sunucudan
  // SÖKÜLDÜ (gerekçede "hiçbir istemcide çağrı yok" yazıyordu ama çağrı
  // duruyordu) ve istek 404 dönüyordu — yani sürükle-bırak ile üye taşıma
  // tamamen ölmüştü. Yerine geçen kaynak-referans ucu üye güncellemesidir:
  // `PATCH /guilds/{guild}/members/{user}` gövdesinde `channel_id`.
  // `channel_id: null` = üyeyi sesten at (aynı uç, MOVE_MEMBERS izni).
  moveMember: (body: { target_user_id: string; server_id: string; channel_id: string | null }) =>
    apiFetch<unknown>(`/guilds/${body.server_id}/members/${body.target_user_id}`, {
      method: "PATCH",
      body: JSON.stringify({ channel_id: body.channel_id }),
    }),
  // Sunucu moderasyonu (MUTE_MEMBERS): üyeyi sunucu-genelinde sustur/sağırlaştır.
  moderate: (body: { target_user_id: string; server_id: string; mute?: boolean; deaf?: boolean }) =>
    apiFetch<{ ok: boolean }>("/voice-states/moderate", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // Sesli kanaldan at (MOVE_MEMBERS): hedefi disconnect eder.
  disconnectMember: (body: { target_user_id: string; server_id: string; channel_id: string }) =>
    apiFetch<{ ok: boolean }>("/voice-states/disconnect-member", {
      method: "POST",
      body: JSON.stringify(body),
    }),
};

/* ── User Folders — G7'de SÖKÜLDÜ: klasörler Kaynak-modeliyle
   PATCH /users/@me/settings guild_folders alanına taşındı
   (GuildFoldersStore). /user-folders REST ucu backend'den de kaldırıldı. ── */

/* ── Scheduled Messages — Feature 13 ─────────────────────────────── */
//
// Backend domain `scheduled_message/` (Migration 0061):
//   POST   /scheduled-messages              — schedule
//   GET    /scheduled-messages              — kullanıcının pending listesi
//   PATCH  /scheduled-messages/{id}
//   DELETE /scheduled-messages/{id}
//
// Worker 30sn'de bir scheduled_at < NOW() olanları sender olarak insert eder.

export interface ScheduledMessageEntry {
  id: string;
  channelId: string;
  content: string;
  fileUrl: string | null;
  scheduledAt: string;
  createdAt: string;
}

export const scheduledMessages = {
  list: () => apiFetch<{ items: ScheduledMessageEntry[] }>("/scheduled-messages"),

  create: (data: {
    channelId: string;
    content: string;
    scheduledAt: string; // ISO 8601 RFC3339
    replyToId?: string;
    fileUrl?: string;
  }) =>
    apiFetch<{ id: string; channelId: string; content: string; scheduledAt: string }>("/scheduled-messages", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: { content?: string; scheduledAt?: string }) =>
    apiFetch<void>(`/scheduled-messages/${id}`, { method: "PATCH", body: JSON.stringify(data) }),

  delete: (id: string) => apiFetch<void>(`/scheduled-messages/${id}`, { method: "DELETE" }),
};

/* ── Saved Messages (Bookmark) — 7.0.4 ───────────────────────────── */
//
// Backend domain `saved_message/` tam hazır:
//   GET    /users/@me/saved-messages?limit=&before=    — pagination cursor
//   POST   /channels/{channelId}/messages/{messageId}/save
//   DELETE /channels/{channelId}/messages/{messageId}/save
//
// Limit: 100/user. WS event'leri: `saved-message:create`, `saved-message:delete`.

export interface SavedMessageEntry {
  id: string;
  messageId: string;
  savedAt: string;
  /** Backend JOIN ile gelir — mesaj silinmişse null veya is_deleted=true ile null. */
  channelId: string | null;
  content: string | null;
  isDeleted: boolean;
  createdAt: string | null;
  author: { id: string; name: string; imageUrl: string } | null;
}

export const savedMessages = {
  list: (params?: { limit?: number; before?: string }) => {
    const qs = new URLSearchParams();
    if (params?.limit !== undefined) qs.set("limit", String(params.limit));
    if (params?.before) qs.set("before", params.before);
    const q = qs.toString();
    // Sunucu kaynak-referans düz dizisini döner; eski `{items}` zarfı da kabul edilir.
    return apiFetch<SavedMessageEntry[] | { items: SavedMessageEntry[] }>(
      `/users/@me/saved-messages${q ? `?${q}` : ""}`,
    ).then((data) => (Array.isArray(data) ? { items: data } : data));
  },
  save: (channelId: string, messageId: string) =>
    apiFetch<{ ok: true }>(`/channels/${channelId}/messages/${messageId}/save`, { method: "POST" }),
  unsave: (channelId: string, messageId: string) =>
    apiFetch<{ ok: true }>(`/channels/${channelId}/messages/${messageId}/save`, { method: "DELETE" }),
};

/* ── Feature flag endpoints (15.3b) ─────────────────────────────── */

export const featureFlags = {
  me: () => apiFetch<{ flags: string[] }>("/feature-flags/me"),

  check: (flagName: string) =>
    apiFetch<{ flag: string; enabled: boolean }>(`/feature-flags/me/${encodeURIComponent(flagName)}`),

  list: () => apiFetch<{ flags: any[] }>("/feature-flags"),

  create: (data: { name: string; description?: string; enabledGlobally?: boolean }) =>
    apiFetch<any>("/feature-flags", { method: "POST", body: JSON.stringify(data) }),

  update: (flagId: string, data: { description?: string; enabledGlobally?: boolean }) =>
    apiFetch<any>(`/feature-flags/${flagId}`, { method: "PATCH", body: JSON.stringify(data) }),

  delete: (flagId: string) => apiFetch<{ success: boolean }>(`/feature-flags/${flagId}`, { method: "DELETE" }),

  // Admin: kullanıcı override (enabled=false → global TRUE olsa bile bu kullanıcıda kapalı).
  grant: (profileId: string, flagName: string, enabled: boolean) =>
    apiFetch<{ success: boolean }>(`/feature-flags/grant`, {
      method: "POST",
      body: JSON.stringify({ profileId, flagName, enabled }),
    }),

  revoke: (profileId: string, flagName: string) =>
    apiFetch<{ success: boolean }>(`/feature-flags/revoke`, {
      method: "POST",
      body: JSON.stringify({ profileId, flagName }),
    }),

  usersWithFlag: (flagId: string) => apiFetch<{ users: any[]; total: number }>(`/feature-flags/${flagId}/users`),
};

/* ── Premium endpoints (15.3a) ─────────────────────────────────────── */

export const premium = {
  // Aktif kullanıcının premium durumu (free/monthly/yearly/lifetime + bitiş + kalan gün)
  me: () =>
    apiFetch<{
      premiumType: number;
      premiumTypeName: string;
      isPremium: boolean;
      premiumSince: string | null;
      premiumUntil: string | null;
      daysRemaining: number | null;
      /** ROZET KADEMESİ (2026-07-26) — bkz. `PremiumBadges.creditedDays`. */
      premiumDays: number;
    }>("/premium/me"),

  // Admin: tüm premium kullanıcıları listele (?type=&limit=&offset=)
  listUsers: (params?: { type?: number; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (params?.type !== undefined) qs.set("type", String(params.type));
    if (params?.limit !== undefined) qs.set("limit", String(params.limit));
    if (params?.offset !== undefined) qs.set("offset", String(params.offset));
    const q = qs.toString();
    return apiFetch<{ items: any[]; total: number }>(`/premium/users${q ? `?${q}` : ""}`);
  },

  // Admin: tek kullanıcının premium detayı
  getUser: (profileId: string) => apiFetch<any>(`/premium/users/${profileId}`),

  // Admin: premium grant (type=1/2/3 ya da explicit days)
  // apiFetch otomatik camelCase → snake_case dönüşümü yapar (profileId → profile_id).
  grant: (profileId: string, premiumType: number, durationDays?: number, reason?: string) =>
    apiFetch<{ success: boolean }>(`/premium/grant`, {
      method: "POST",
      body: JSON.stringify({ profileId, premiumType, durationDays, reason }),
    }),

  // Admin: premium revoke
  revoke: (profileId: string, reason?: string) =>
    apiFetch<{ success: boolean }>(`/premium/revoke`, {
      method: "POST",
      body: JSON.stringify({ profileId, reason }),
    }),
};

/* ── OAuth2 yetkilendirmeleri (12 Eyl 2026, ayarlar parite turu) ─────────
   kaynak-referans OAuth2AuthorizationCommands 1-1: kullanıcının hesabına
   erişim verdiği uygulamalar (Hesap → Güvenlik → Hesap erişimi → Yönet).
   Backend: oauth2/mod.rs `/oauth2/@me/authorizations` (list / revoke / bulk). */

export interface OAuth2AuthorizationRecord {
  application: { id: string; name: string; icon?: string | null };
  scopes: string[];
}

export const oauth2Authorizations = {
  list: () => apiFetch<OAuth2AuthorizationRecord[]>("/oauth2/@me/authorizations"),
  revoke: (applicationId: string) =>
    apiFetch<void>(`/oauth2/@me/authorizations/${applicationId}`, { method: "DELETE" }),
  revokeMany: (applicationIds: string[]) =>
    apiFetch<void>("/oauth2/@me/authorizations/revoke", {
      method: "POST",
      body: JSON.stringify({ application_ids: applicationIds }),
    }),
};

/* ── Push abonelikleri (cihaz listesi) — 12 Eyl 2026 ayarlar parite turu ──
   kaynak-referans usePushSubscriptions: bu hesabın kayıtlı push cihazları.
   Backend: push/mod.rs `/users/@me/push/subscriptions` (list / delete). */

export interface PushDeviceRecord {
  deviceId: string;
  platform?: string | null;
  appId?: string | null;
  providerEnvironment?: string | null;
  userAgent?: string | null;
}

export const pushDevices = {
  list: () => apiFetch<{ devices: PushDeviceRecord[] }>("/users/@me/push/subscriptions"),
  remove: (deviceId: string) => apiFetch<void>(`/users/@me/push/subscriptions/${deviceId}`, { method: "DELETE" }),
};
